/*
 * Infrastructure Management System - Main App Component
 * Version: 4.15.0
 * © 2025 DeskShare Inc. All rights reserved.
 * 
 * Changelog v4.15.0:
 * - Fixed hardware display: Memory MB→GB conversion (OVH returns MB)
 * - Fixed hardware display: Prevented "0" rendering for empty disk values
 * - Fixed hypervisor server dropdown empty issue
 * - Fixed dashboard metrics error handling with better feedback
 * - Improved cache key generation for server list API
 * - Added helpful empty state messages throughout
 * 
 * Changelog v4.14.0:
 * - Added VM Statistics summary section to Dashboard
 * - Added auto-refresh hook for real-time data updates
 * - Added auto-refresh for VM metrics tab (running VMs only)
 * - Added visual refresh indicator with timing display
 * - Added resource totals and average metrics display
 * - Improved edge case handling and loading states
 * 
 * Changelog v4.13.0:
 * - Added VM Metrics History tab with time-series charts
 * - Added CPU, Memory, Disk usage charts with area visualization
 * - Added Network I/O and Disk I/O line charts
 * - Added time range selector (1h, 6h, 24h, 7d)
 * - Added "View Metrics History" link from current usage section
 * 
 * Changelog v4.12.1:
 * - Added VM count badge on server cards (running/total)
 * - Added VMs tab in server detail modal
 * - Added cross-navigation between servers and VMs
 * - Added server filter banner when viewing VMs from server
 * - Added host server link in VM detail modal
 * 
 * Changelog v4.12.0:
 * - Added Virtual Machines page with list, filtering, and search
 * - Added VM Card component with state indicators and usage metrics
 * - Added VM Detail Modal with comprehensive VM information
 * - Added VM statistics summary bar
 * 
 * Changelog v4.11.0:
 * - Added Hypervisors page for managing Proxmox/Hyper-V configurations
 * - Added Hypervisor config modal with type-specific fields
 * - Added Test Connection and Sync VMs functionality
 * 
 * Changelog v4.8.0:
 * - Added Metrics Dashboard with historical charts
 * - Added time-series visualization using Recharts
 * 
 * Changelog v4.7.0:
 * - Added Alerts page with alert stats, history, and management
 * - Added notification channel configuration UI
 * - Added alert rule management UI
 */

import { useState, useEffect, createContext, useContext, useCallback, useRef } from 'react'
import { BrowserRouter as Router, Routes, Route, Link, Navigate, useNavigate } from 'react-router-dom'
import axios from 'axios'
import { 
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer 
} from 'recharts'
import './App.css'

// =============================================================================
// API Configuration
// =============================================================================

const api = axios.create({
  baseURL: import.meta.env.VITE_API_URL || window.location.origin,
  withCredentials: true, // Required for session cookies
  headers: {
    'Content-Type': 'application/json',
  }
});

// Request interceptor to add CSRF token
api.interceptors.request.use(
  async (config) => {
    // Add CSRF token for non-GET requests
    if (['post', 'put', 'patch', 'delete'].includes(config.method?.toLowerCase())) {
      const csrfToken = getCsrfToken();
      if (csrfToken) {
        config.headers['X-CSRFToken'] = csrfToken;
      } else {
        console.warn('CSRF token not found in cookies. Request may fail with 403.');
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor for error handling
api.interceptors.response.use(
  response => response,
  error => {
    if (error.response?.status === 401) {
      console.warn('Session expired or not authenticated');
    } else if (error.response?.status === 403) {
      console.warn('Access denied:', error.response.data?.error || 'Permission denied');
    }
    return Promise.reject(error);
  }
);

// Get CSRF token from cookie
function getCsrfToken() {
  const name = 'csrftoken';
  const cookies = document.cookie.split(';');
  for (let cookie of cookies) {
    const trimmed = cookie.trim();
    // Use indexOf to handle values that might contain '='
    const eqIndex = trimmed.indexOf('=');
    if (eqIndex > 0) {
      const key = trimmed.substring(0, eqIndex);
      const value = trimmed.substring(eqIndex + 1);
      if (key === name) {
        // Decode in case of URL encoding
        return decodeURIComponent(value);
      }
    }
  }
  return null;
}

// =============================================================================
// Auto-Refresh Hook
// =============================================================================

/**
 * Custom hook for auto-refreshing data at a configurable interval.
 * 
 * @param {Function} fetchFn - The function to call for refreshing data
 * @param {Object} options - Configuration options
 * @param {number} options.interval - Refresh interval in milliseconds (default: 30000)
 * @param {boolean} options.enabled - Whether auto-refresh is enabled (default: false)
 * @param {boolean} options.immediate - Whether to fetch immediately on enable (default: false)
 * @returns {Object} - Control functions and state
 */
function useAutoRefresh(fetchFn, options = {}) {
  const {
    interval = 30000,
    enabled = false,
    immediate = false,
  } = options;

  const [isRefreshing, setIsRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [error, setError] = useState(null);
  const [isEnabled, setIsEnabled] = useState(enabled);
  const intervalRef = useRef(null);
  const fetchRef = useRef(fetchFn);

  // Keep fetch function reference current
  useEffect(() => {
    fetchRef.current = fetchFn;
  }, [fetchFn]);

  // Perform refresh
  const doRefresh = useCallback(async () => {
    if (isRefreshing) return;
    
    setIsRefreshing(true);
    setError(null);
    
    try {
      await fetchRef.current();
      setLastRefresh(new Date());
    } catch (err) {
      setError(err);
      console.error('Auto-refresh failed:', err);
    } finally {
      setIsRefreshing(false);
    }
  }, [isRefreshing]);

  // Manual refresh
  const refresh = useCallback(() => {
    doRefresh();
  }, [doRefresh]);

  // Start auto-refresh
  const start = useCallback(() => {
    setIsEnabled(true);
  }, []);

  // Stop auto-refresh
  const stop = useCallback(() => {
    setIsEnabled(false);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Toggle auto-refresh
  const toggle = useCallback(() => {
    setIsEnabled(prev => !prev);
  }, []);

  // Setup/cleanup interval
  useEffect(() => {
    if (isEnabled) {
      // Immediate fetch if requested
      if (immediate && !lastRefresh) {
        doRefresh();
      }
      
      // Setup interval
      intervalRef.current = setInterval(doRefresh, interval);
      
      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      };
    } else {
      // Clear interval when disabled
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }
  }, [isEnabled, interval, immediate, doRefresh, lastRefresh]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  return {
    isRefreshing,
    lastRefresh,
    error,
    isEnabled,
    refresh,
    start,
    stop,
    toggle,
  };
}

// =============================================================================
// Auth Context
// =============================================================================

const AuthContext = createContext(null);

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Fetch CSRF token on mount
  const fetchCsrfToken = useCallback(async () => {
    try {
      await api.get('/api/auth/csrf/');
    } catch (err) {
      console.error('Failed to fetch CSRF token:', err);
    }
  }, []);

  // Check authentication status
  const checkAuth = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/auth/user/');
      if (response.data.authenticated) {
        setUser(response.data.user);
      } else {
        setUser(null);
      }
      setError(null);
    } catch (err) {
      console.error('Auth check failed:', err);
      setUser(null);
      setError('Failed to check authentication status');
    } finally {
      setLoading(false);
    }
  }, []);

  // Login
  const login = async (username, password) => {
    try {
      // Ensure we have CSRF token
      await fetchCsrfToken();
      
      const response = await api.post('/api/auth/login/', { username, password });
      if (response.data.success) {
        setUser(response.data.user);
        setError(null);
        return { success: true };
      }
      return { success: false, error: 'Login failed' };
    } catch (err) {
      const errorMsg = err.response?.data?.details || 
                       err.response?.data?.error || 
                       'Invalid username or password';
      setError(errorMsg);
      return { success: false, error: errorMsg };
    }
  };

  // Logout
  const logout = async () => {
    try {
      await api.post('/api/auth/logout/');
      setUser(null);
      return { success: true };
    } catch (err) {
      console.error('Logout failed:', err);
      // Still clear user state even if logout request fails
      setUser(null);
      return { success: false, error: 'Logout failed' };
    }
  };

  // Initialize auth state
  useEffect(() => {
    const init = async () => {
      await fetchCsrfToken();
      await checkAuth();
    };
    init();
  }, [fetchCsrfToken, checkAuth]);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    checkAuth,
    isAuthenticated: !!user,
    isAdmin: user?.is_superuser || false,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// Protected Route Component
// =============================================================================

function ProtectedRoute({ children, requireAdmin = false }) {
  const { isAuthenticated, isAdmin, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

// =============================================================================
// Main App Component
// =============================================================================

function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

function AppContent() {
  const { user, loading, isAuthenticated, logout, isAdmin } = useAuth();
  const [healthStatus, setHealthStatus] = useState(null);
  const [healthLoading, setHealthLoading] = useState(true);
  const [healthError, setHealthError] = useState(null);

  useEffect(() => {
    checkHealth();
  }, []);

  const checkHealth = async () => {
    try {
      setHealthLoading(true);
      const response = await api.get('/health/');
      setHealthStatus(response.data);
      setHealthError(null);
    } catch (err) {
      setHealthError('Failed to connect to backend API');
      console.error('Health check failed:', err);
    } finally {
      setHealthLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
  };

  if (loading) {
    return (
      <div className="app">
        <div className="loading-screen">
          <h2>Loading...</h2>
        </div>
      </div>
    );
  }

  return (
    <div className="app">
      <header className="app-header">
        <h1>🖥️ Infrastructure Management System</h1>
        <nav>
          <Link to="/">Dashboard</Link>
          <Link to="/servers">Servers</Link>
          <Link to="/vms">Virtual Machines</Link>
          {isAdmin && <Link to="/hypervisors">Hypervisors</Link>}
          <Link to="/alerts">Alerts</Link>
          <Link to="/about">About</Link>
          {isAuthenticated ? (
            <div className="user-nav">
              <span className="user-info">
                👤 {user.username}
                {isAdmin && <span className="admin-badge">Admin</span>}
              </span>
              <button onClick={handleLogout} className="logout-btn">
                Logout
              </button>
            </div>
          ) : (
            <Link to="/login" className="login-link">Login</Link>
          )}
        </nav>
      </header>

      <main className="app-main">
        <Routes>
          <Route 
            path="/" 
            element={
              <Dashboard 
                healthStatus={healthStatus} 
                loading={healthLoading} 
                error={healthError} 
              />
            } 
          />
          <Route path="/servers" element={<Servers />} />
          <Route path="/vms" element={<VirtualMachines />} />
          <Route 
            path="/hypervisors" 
            element={
              <ProtectedRoute requireAdmin={true}>
                <Hypervisors />
              </ProtectedRoute>
            } 
          />
          <Route path="/alerts" element={<Alerts />} />
          <Route path="/about" element={<About />} />
          <Route path="/login" element={<Login />} />
        </Routes>
      </main>

      <footer className="app-footer">
        <p>© 2025 DeskShare Inc. All rights reserved.</p>
        <p>Version 4.12.0</p>
      </footer>
    </div>
  );
}

// =============================================================================
// Login Component
// =============================================================================

function Login() {
  const { login, isAuthenticated, error: authError } = useAuth();
  const navigate = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate('/');
    }
  }, [isAuthenticated, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    if (!username || !password) {
      setError('Please enter both username and password');
      return;
    }

    setLoading(true);
    const result = await login(username, password);
    setLoading(false);

    if (result.success) {
      navigate('/');
    } else {
      setError(result.error || 'Login failed');
    }
  };

  return (
    <div className="login-page">
      <div className="login-card">
        <h2>Login</h2>
        <p className="login-subtitle">Sign in to access the Infrastructure Management System</p>
        
        {(error || authError) && (
          <div className="error-message">
            <p className="error">{error || authError}</p>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form">
          <div className="form-group">
            <label htmlFor="username">Username</label>
            <input
              type="text"
              id="username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              placeholder="Enter your username"
              autoComplete="username"
              disabled={loading}
            />
          </div>
          
          <div className="form-group">
            <label htmlFor="password">Password</label>
            <input
              type="password"
              id="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button type="submit" className="login-btn" disabled={loading}>
            {loading ? 'Signing in...' : 'Sign In'}
          </button>
        </form>

        <div className="login-help">
          <p>Need access? Contact your system administrator.</p>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Dashboard Component
// =============================================================================

function Dashboard({ healthStatus, loading, error }) {
  const [stats, setStats] = useState(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsRange, setMetricsRange] = useState('7d');
  
  // VM Stats state
  const [vmStats, setVmStats] = useState(null);
  const [vmStatsLoading, setVmStatsLoading] = useState(true);
  
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Chart colors
  const COLORS = {
    critical: '#ff4757',
    warning: '#ffa502',
    info: '#3498db',
    active: '#2ed573',
    maintenance: '#ff9500',
    inactive: '#ff6b7a',
    primary: '#667eea',
    secondary: '#764ba2',
    running: '#2ed573',
    stopped: '#ff6b7a',
    proxmox: '#e57000',
    hyperv: '#0078d4',
  };

  useEffect(() => {
    if (isAuthenticated) {
      fetchStats();
      fetchMetricsDashboard();
      fetchVMStats();
    } else {
      setStatsLoading(false);
      setVmStatsLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchMetricsDashboard();
    }
  }, [metricsRange]);

  const fetchStats = async () => {
    try {
      setStatsLoading(true);
      const response = await api.get('/api/servers/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
    } finally {
      setStatsLoading(false);
    }
  };

  const fetchMetricsDashboard = async () => {
    try {
      setMetricsLoading(true);
      const response = await api.get(`/api/metrics/dashboard/?range=${metricsRange}`);
      setMetricsData(response.data);
    } catch (err) {
      console.error('Failed to fetch metrics dashboard:', err);
      // Set empty data instead of null to show "no data" message rather than error
      setMetricsData({
        alert_timeline: [],
        server_status: [],
        alert_by_type: [],
        metrics_frequency: [],
        error: err.response?.data?.error || 'Failed to load metrics'
      });
    } finally {
      setMetricsLoading(false);
    }
  };

  const fetchVMStats = async () => {
    try {
      setVmStatsLoading(true);
      const response = await api.get('/api/vms/stats/');
      setVmStats(response.data);
    } catch (err) {
      console.error('Failed to fetch VM stats:', err);
      // Don't set error state - VM stats are optional
    } finally {
      setVmStatsLoading(false);
    }
  };

  // Format bytes for VM resource display
  const formatVMBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  };

  // Format date for chart display
  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return metricsRange === '24h' 
      ? date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      : date.toLocaleDateString([], { month: 'short', day: 'numeric' });
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Get CSS class for metric bars based on value
  const getMetricClass = (value) => {
    if (value >= 90) return 'critical';
    if (value >= 75) return 'warning';
    return 'normal';
  };

  return (
    <div className="dashboard">
      <h2>System Dashboard</h2>
      
      {!isAuthenticated && (
        <div className="auth-notice">
          <p>🔒 <Link to="/login">Sign in</Link> to view server statistics and manage infrastructure.</p>
        </div>
      )}
      
      <div className="dashboard-grid">
        <div className="status-card">
          <h3>Backend Status</h3>
          {loading && <p>Checking backend status...</p>}
          {error && <p className="error">{error}</p>}
          {healthStatus && (
            <div className="health-status">
              <p className="status-indicator success">
                ✓ Backend: {healthStatus.status}
              </p>
              {healthStatus.checks?.database && (
                <p className={`status-indicator ${healthStatus.checks.database.status === 'healthy' ? 'success' : 'warning'}`}>
                  {healthStatus.checks.database.status === 'healthy' ? '✓' : '⚠'} Database: {healthStatus.checks.database.status}
                </p>
              )}
              {healthStatus.checks?.cache && (
                <p className={`status-indicator ${healthStatus.checks.cache.status === 'healthy' ? 'success' : 'warning'}`}>
                  {healthStatus.checks.cache.status === 'healthy' ? '✓' : '⚠'} Cache: {healthStatus.checks.cache.status}
                </p>
              )}
              {healthStatus.checks?.ovh_api && (
                <p className={`status-indicator ${healthStatus.checks.ovh_api.status === 'healthy' ? 'success' : healthStatus.checks.ovh_api.status === 'not_configured' ? 'warning' : 'error'}`}>
                  {healthStatus.checks.ovh_api.status === 'healthy' ? '✓' : healthStatus.checks.ovh_api.status === 'not_configured' ? '⚠' : '✗'} OVH API: {healthStatus.checks.ovh_api.status}
                </p>
              )}
            </div>
          )}
        </div>

        <div className="status-card">
          <h3>Server Statistics</h3>
          {!isAuthenticated ? (
            <p className="login-prompt">Sign in to view statistics</p>
          ) : statsLoading ? (
            <p>Loading statistics...</p>
          ) : stats ? (
            <div className="stats-grid">
              <div className="stat-item">
                <span className="stat-value">{stats.total_servers || 0}</span>
                <span className="stat-label">Total Servers</span>
              </div>
              <div className="stat-item">
                <span className="stat-value success">{stats.active_servers || 0}</span>
                <span className="stat-label">Active</span>
              </div>
              <div className="stat-item">
                <span className="stat-value warning">{stats.maintenance_servers || 0}</span>
                <span className="stat-label">Maintenance</span>
              </div>
              <div className="stat-item">
                <span className="stat-value error">{stats.inactive_servers || 0}</span>
                <span className="stat-label">Inactive</span>
              </div>
            </div>
          ) : (
            <p>No statistics available. <Link to="/servers">Discover servers</Link> first.</p>
          )}
        </div>
      </div>

      {/* VM Statistics Section */}
      {isAuthenticated && (
        <div className="vm-stats-dashboard-section">
          <div className="vm-stats-header">
            <h3>🖥️ Virtual Machine Statistics</h3>
            <button 
              className="btn-link vm-stats-link"
              onClick={() => navigate('/vms')}
            >
              View All VMs →
            </button>
          </div>
          
          {vmStatsLoading ? (
            <div className="vm-stats-loading">Loading VM statistics...</div>
          ) : vmStats && vmStats.total_vms > 0 ? (
            <div className="vm-stats-content">
              {/* VM Count Summary */}
              <div className="vm-stats-summary">
                <div className="vm-stat-card">
                  <div className="vm-stat-icon">💻</div>
                  <div className="vm-stat-info">
                    <span className="vm-stat-value">{vmStats.total_vms}</span>
                    <span className="vm-stat-label">Total VMs</span>
                  </div>
                </div>
                <div className="vm-stat-card running">
                  <div className="vm-stat-icon">▶️</div>
                  <div className="vm-stat-info">
                    <span className="vm-stat-value">{vmStats.running_count || 0}</span>
                    <span className="vm-stat-label">Running</span>
                  </div>
                </div>
                <div className="vm-stat-card stopped">
                  <div className="vm-stat-icon">⏹️</div>
                  <div className="vm-stat-info">
                    <span className="vm-stat-value">{vmStats.stopped_count || 0}</span>
                    <span className="vm-stat-label">Stopped</span>
                  </div>
                </div>
              </div>

              {/* Resource Totals (Running VMs) */}
              {vmStats.resource_totals && vmStats.running_count > 0 && (
                <div className="vm-resources-section">
                  <h4>🔧 Resources (Running VMs)</h4>
                  <div className="vm-resources-grid">
                    <div className="vm-resource-item">
                      <span className="resource-icon">🖥️</span>
                      <span className="resource-value">{vmStats.resource_totals.cpu_cores || 0}</span>
                      <span className="resource-label">vCPU Cores</span>
                    </div>
                    <div className="vm-resource-item">
                      <span className="resource-icon">💾</span>
                      <span className="resource-value">{vmStats.resource_totals.memory_gb || 0} GB</span>
                      <span className="resource-label">Memory</span>
                    </div>
                    <div className="vm-resource-item">
                      <span className="resource-icon">💿</span>
                      <span className="resource-value">{vmStats.resource_totals.disk_gb || 0} GB</span>
                      <span className="resource-label">Disk</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Average Metrics (Running VMs) */}
              {vmStats.average_metrics && vmStats.running_count > 0 && (
                <div className="vm-metrics-section">
                  <h4>📊 Average Usage (Running VMs)</h4>
                  <div className="vm-metrics-bars">
                    <div className="vm-metric-bar">
                      <div className="metric-bar-header">
                        <span>CPU</span>
                        <span>{vmStats.average_metrics.cpu_usage || 0}%</span>
                      </div>
                      <div className="metric-bar-container">
                        <div 
                          className={`metric-bar-fill ${getMetricClass(vmStats.average_metrics.cpu_usage)}`}
                          style={{ width: `${Math.min(vmStats.average_metrics.cpu_usage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="vm-metric-bar">
                      <div className="metric-bar-header">
                        <span>Memory</span>
                        <span>{vmStats.average_metrics.memory_usage || 0}%</span>
                      </div>
                      <div className="metric-bar-container">
                        <div 
                          className={`metric-bar-fill ${getMetricClass(vmStats.average_metrics.memory_usage)}`}
                          style={{ width: `${Math.min(vmStats.average_metrics.memory_usage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                    <div className="vm-metric-bar">
                      <div className="metric-bar-header">
                        <span>Disk</span>
                        <span>{vmStats.average_metrics.disk_usage || 0}%</span>
                      </div>
                      <div className="metric-bar-container">
                        <div 
                          className={`metric-bar-fill ${getMetricClass(vmStats.average_metrics.disk_usage)}`}
                          style={{ width: `${Math.min(vmStats.average_metrics.disk_usage || 0, 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Hypervisor Distribution */}
              {vmStats.by_hypervisor && (
                <div className="vm-hypervisor-section">
                  <h4>🔌 By Hypervisor</h4>
                  <div className="vm-hypervisor-badges">
                    {vmStats.by_hypervisor.proxmox > 0 && (
                      <span className="hypervisor-badge proxmox">
                        Proxmox: {vmStats.by_hypervisor.proxmox}
                      </span>
                    )}
                    {vmStats.by_hypervisor.hyperv > 0 && (
                      <span className="hypervisor-badge hyperv">
                        Hyper-V: {vmStats.by_hypervisor.hyperv}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="vm-stats-empty">
              <p>No virtual machines discovered yet.</p>
              <p className="hint">
                <Link to="/hypervisors">Configure hypervisors</Link> to start discovering VMs.
              </p>
            </div>
          )}
        </div>
      )}

      {/* Metrics Dashboard Charts */}
      {isAuthenticated && (
        <div className="metrics-dashboard-section">
          <div className="metrics-header">
            <h3>📊 Metrics Dashboard</h3>
            <div className="time-range-selector">
              <button 
                className={`range-btn ${metricsRange === '24h' ? 'active' : ''}`}
                onClick={() => setMetricsRange('24h')}
              >24h</button>
              <button 
                className={`range-btn ${metricsRange === '7d' ? 'active' : ''}`}
                onClick={() => setMetricsRange('7d')}
              >7d</button>
              <button 
                className={`range-btn ${metricsRange === '30d' ? 'active' : ''}`}
                onClick={() => setMetricsRange('30d')}
              >30d</button>
              <button 
                className={`range-btn ${metricsRange === '90d' ? 'active' : ''}`}
                onClick={() => setMetricsRange('90d')}
              >90d</button>
            </div>
          </div>

          {metricsLoading ? (
            <div className="metrics-loading">Loading metrics data...</div>
          ) : metricsData ? (
            <div className="charts-grid">
              {/* Alert Timeline Chart */}
              {metricsData.alert_timeline?.length > 0 && (
                <div className="chart-card">
                  <h4>Alert Activity</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={metricsData.alert_timeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        stroke="#666"
                        fontSize={12}
                      />
                      <YAxis stroke="#666" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend />
                      <Bar dataKey="critical" stackId="a" fill={COLORS.critical} name="Critical" />
                      <Bar dataKey="warning" stackId="a" fill={COLORS.warning} name="Warning" />
                      <Bar dataKey="info" stackId="a" fill={COLORS.info} name="Info" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Server Status Distribution */}
              {metricsData.server_status?.length > 0 && (
                <div className="chart-card">
                  <h4>Server Status Distribution</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <PieChart>
                      <Pie
                        data={metricsData.server_status}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name} (${(percent * 100).toFixed(0)}%)`}
                        outerRadius={80}
                        fill="#8884d8"
                        dataKey="count"
                        nameKey="status"
                      >
                        {metricsData.server_status.map((entry, index) => (
                          <Cell 
                            key={`cell-${index}`} 
                            fill={COLORS[entry.status] || COLORS.primary} 
                          />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Alert Type Distribution */}
              {metricsData.alert_by_type?.length > 0 && (
                <div className="chart-card">
                  <h4>Alerts by Type</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <BarChart data={metricsData.alert_by_type} layout="vertical">
                      <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                      <XAxis type="number" stroke="#666" fontSize={12} />
                      <YAxis 
                        dataKey="type" 
                        type="category" 
                        stroke="#666" 
                        fontSize={11}
                        width={120}
                      />
                      <Tooltip />
                      <Bar dataKey="count" fill={COLORS.primary} name="Count" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Metrics Collection Frequency */}
              {metricsData.metrics_frequency?.length > 0 && (
                <div className="chart-card">
                  <h4>Metrics Collection</h4>
                  <ResponsiveContainer width="100%" height={250}>
                    <LineChart data={metricsData.metrics_frequency}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                      <XAxis 
                        dataKey="date" 
                        tickFormatter={formatDate}
                        stroke="#666"
                        fontSize={12}
                      />
                      <YAxis stroke="#666" fontSize={12} />
                      <Tooltip content={<CustomTooltip />} />
                      <Line 
                        type="monotone" 
                        dataKey="collections" 
                        stroke={COLORS.primary} 
                        strokeWidth={2}
                        dot={{ fill: COLORS.primary, strokeWidth: 2 }}
                        name="Collections"
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* No Data Message */}
              {!metricsData.alert_timeline?.length && 
               !metricsData.server_status?.length && 
               !metricsData.metrics_frequency?.length && (
                <div className="no-metrics-data">
                  <p>No metrics data available for the selected time range.</p>
                  <p>Data will appear here as alerts are triggered and metrics are collected.</p>
                </div>
              )}
            </div>
          ) : (
            <div className="no-metrics-data">
              <p>📊 No metrics data to display yet.</p>
              <p>Metrics will appear as servers are discovered and alerts are triggered.</p>
              <p><Link to="/servers">Go to Servers</Link> to refresh server metrics.</p>
            </div>
          )}
        </div>
      )}

      <div className="info-card">
        <h3>Quick Start</h3>
        <ol>
          <li>Sign in with your administrator credentials</li>
          <li>Verify OVH API connection in Backend Status</li>
          <li>Navigate to <Link to="/servers">Servers</Link> and click "Discover Servers"</li>
          <li>Monitor your infrastructure from the dashboard</li>
        </ol>
        {isAdmin && (
          <p className="admin-note">
            ✓ You have administrator privileges and can discover servers.
          </p>
        )}
      </div>
    </div>
  );
}

// =============================================================================
// Servers Component
// =============================================================================

function Servers() {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [refreshingMetrics, setRefreshingMetrics] = useState(false);
  const [error, setError] = useState(null);
  const [discoveryResult, setDiscoveryResult] = useState(null);
  const [selectedServer, setSelectedServer] = useState(null);
  const [serverDetail, setServerDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  // Batch refresh progress tracking
  const [batchJob, setBatchJob] = useState(null);
  const [pollInterval, setPollInterval] = useState(null);

  // Navigate to VMs page filtered by server
  const handleNavigateToVMs = (serverId) => {
    navigate(`/vms?host_server=${serverId}`);
  };
  
  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (pollInterval) {
        clearInterval(pollInterval);
      }
    };
  }, [pollInterval]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchServers();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchServers = async () => {
    try {
      setLoading(true);
      setError(null);
      // Use enhanced=true to get metrics with server list
      const response = await api.get('/api/servers/?enhanced=true');
      setServers(response.data.results || []);
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please sign in to view servers.');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view servers.');
      } else if (err.response?.status === 500) {
        setError('Server error. Please try again later.');
      } else {
        setError('Failed to fetch servers. Please check your connection.');
      }
      console.error('Failed to fetch servers:', err);
    } finally {
      setLoading(false);
    }
  };

  const discoverServers = async () => {
    try {
      setDiscovering(true);
      setError(null);
      setDiscoveryResult(null);
      const response = await api.post('/api/servers/discover/');
      setDiscoveryResult(response.data);
      await fetchServers();
    } catch (err) {
      if (err.response?.status === 401) {
        setError('Please sign in to discover servers.');
      } else if (err.response?.status === 403) {
        setError('Admin privileges required for server discovery. Current user does not have administrator access.');
      } else if (err.response?.status === 503) {
        setError('OVH API not configured. Please set OVH credentials in environment.');
      } else if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait before trying again.');
      } else {
        setError(err.response?.data?.error || 'Discovery failed. Please check the logs.');
      }
      console.error('Discovery failed:', err);
    } finally {
      setDiscovering(false);
    }
  };
  
  // Poll for batch job status
  const pollBatchStatus = async (jobId) => {
    try {
      const response = await api.get(`/api/servers/batch_status/${jobId}/`);
      const job = response.data;
      setBatchJob(job);
      
      // Check if job is complete
      if (!job.is_active) {
        // Stop polling
        if (pollInterval) {
          clearInterval(pollInterval);
          setPollInterval(null);
        }
        setRefreshingMetrics(false);
        
        // Show result
        if (job.status === 'completed') {
          setDiscoveryResult({
            new_servers: 0,
            updated_servers: job.success_count,
            total_servers: job.total_items,
            message: `Refreshed metrics for ${job.success_count} servers` + 
                    (job.failed_count > 0 ? `, ${job.failed_count} failed` : ''),
          });
          // Refresh server list to show updated data
          await fetchServers();
        } else if (job.status === 'cancelled') {
          setError(`Refresh cancelled at ${job.processed_items}/${job.total_items} servers.`);
        } else if (job.status === 'failed') {
          setError('Refresh failed. Check server logs for details.');
        }
        
        // Clear batch job state after a delay to allow user to see final state
        setTimeout(() => setBatchJob(null), 5000);
      }
    } catch (err) {
      console.error('Failed to poll batch status:', err);
      // Don't stop polling on transient errors, but log them
    }
  };

  const refreshAllMetrics = async () => {
    try {
      setRefreshingMetrics(true);
      setError(null);
      setDiscoveryResult(null);
      setBatchJob(null);
      
      const response = await api.post('/api/servers/refresh_all_metrics/');
      
      // Check if this is an async response (HTTP 202)
      if (response.data.async && response.data.job_id) {
        const jobId = response.data.job_id;
        
        // Initialize batch job state
        setBatchJob({
          id: jobId,
          status: 'pending',
          total_items: response.data.total_servers,
          processed_items: 0,
          success_count: 0,
          failed_count: 0,
          current_item: '',
          progress_percent: 0,
          is_active: true,
        });
        
        // Start polling for status updates
        const interval = setInterval(() => pollBatchStatus(jobId), 1500);
        setPollInterval(interval);
        
        // Initial poll
        await pollBatchStatus(jobId);
      } else {
        // Fallback for synchronous response (shouldn't happen with new backend)
        setDiscoveryResult({
          new_servers: 0,
          updated_servers: response.data.success_count || 0,
          total_servers: (response.data.success_count || 0) + (response.data.failed_count || 0),
          message: response.data.message,
        });
        setRefreshingMetrics(false);
        await fetchServers();
      }
    } catch (err) {
      setRefreshingMetrics(false);
      if (err.response?.status === 429) {
        setError('Rate limit exceeded. Please wait before refreshing metrics again.');
      } else if (err.response?.status === 409) {
        // Job already running - show existing job status
        setError(err.response?.data?.details || 'A refresh operation is already in progress.');
        if (err.response?.data?.job_id) {
          // Start polling the existing job
          setBatchJob({
            id: err.response.data.job_id,
            progress_percent: err.response.data.progress_percent || 0,
            is_active: true,
          });
          const interval = setInterval(() => pollBatchStatus(err.response.data.job_id), 1500);
          setPollInterval(interval);
          setRefreshingMetrics(true);
        }
      } else {
        setError(err.response?.data?.error || 'Failed to refresh metrics.');
      }
      console.error('Metrics refresh failed:', err);
    }
  };
  
  const cancelBatchRefresh = async () => {
    if (!batchJob?.id) return;
    
    try {
      await api.post(`/api/servers/batch_cancel/${batchJob.id}/`);
      // The polling will handle the status update
    } catch (err) {
      console.error('Failed to cancel batch refresh:', err);
      setError('Failed to cancel refresh operation.');
    }
  };

  const fetchServerDetail = async (serverId) => {
    try {
      setDetailLoading(true);
      const response = await api.get(`/api/servers/${serverId}/`);
      setServerDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch server detail:', err);
      setServerDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshServerMetrics = async (serverId) => {
    try {
      setDetailLoading(true);
      setError(null);
      const response = await api.post(`/api/servers/${serverId}/refresh_metrics/`);
      if (response.data.success) {
        await fetchServerDetail(serverId);
        await fetchServers();
      }
    } catch (err) {
      console.error('Failed to refresh server metrics:', err);
      let errorMsg = 'Failed to refresh metrics for this server.';
      if (err.response?.status === 429) {
        errorMsg = 'Rate limit exceeded. Please wait before refreshing again.';
      } else if (err.response?.status === 403) {
        errorMsg = 'Permission denied. Admin access required.';
      } else if (err.response?.status === 400) {
        errorMsg = err.response?.data?.details || err.response?.data?.error || 'Metrics refresh not supported for this server.';
      } else if (err.response?.status === 503) {
        errorMsg = err.response?.data?.details || 'OVH API not configured or unavailable.';
      } else if (err.response?.data?.details) {
        errorMsg = err.response.data.details;
      } else if (err.response?.data?.error) {
        errorMsg = err.response.data.error;
      }
      setError(errorMsg);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleServerClick = (server) => {
    setSelectedServer(server);
    fetchServerDetail(server.id);
  };

  const closeDetail = () => {
    setSelectedServer(null);
    setServerDetail(null);
  };

  if (!isAuthenticated) {
    return (
      <div className="servers">
        <h2>Server List</h2>
        <div className="auth-notice">
          <p>🔒 Please <Link to="/login">sign in</Link> to view and manage servers.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="servers">
      <div className="servers-header">
        <h2>Server List</h2>
        <div className="servers-actions">
          <button onClick={fetchServers} disabled={loading || discovering || refreshingMetrics}>
            {loading ? 'Loading...' : 'Refresh'}
          </button>
          {isAdmin && (
            <button 
              onClick={refreshAllMetrics} 
              disabled={loading || discovering || refreshingMetrics}
              className="metrics-btn"
            >
              {refreshingMetrics ? 'Refreshing...' : 'Refresh Metrics'}
            </button>
          )}
          {isAdmin ? (
            <button 
              onClick={discoverServers} 
              disabled={loading || discovering || refreshingMetrics}
              className="discover-btn"
            >
              {discovering ? 'Discovering...' : 'Discover Servers'}
            </button>
          ) : (
            <button className="discover-btn disabled" disabled title="Admin privileges required">
              Discover Servers (Admin Only)
            </button>
          )}
        </div>
      </div>

      {!isAdmin && (
        <div className="info-message">
          <p>ℹ️ Server discovery requires administrator privileges. Contact your admin to discover new servers.</p>
        </div>
      )}

      {discoveryResult && (
        <div className="discovery-result success">
          <p>✓ {discoveryResult.message || `Discovery completed: ${discoveryResult.new_servers} new, ${discoveryResult.updated_servers} updated, ${discoveryResult.total_servers} total`}</p>
        </div>
      )}

      {error && (
        <div className="error-message">
          <p className="error">{error}</p>
        </div>
      )}

      {loading ? (
        <p>Loading servers...</p>
      ) : discovering ? (
        <div className="discovering-message">
          <p>🔍 Discovering servers from OVH API...</p>
          <p className="hint">This may take a moment depending on the number of servers.</p>
        </div>
      ) : refreshingMetrics ? (
        <div className="batch-refresh-progress">
          <div className="progress-header">
            <p>📊 Refreshing metrics from OVH API...</p>
            {batchJob?.current_item && (
              <p className="current-item">Processing: {batchJob.current_item}</p>
            )}
          </div>
          
          {batchJob && (
            <>
              <div className="progress-bar-container">
                <div 
                  className="progress-bar-fill" 
                  style={{ width: `${batchJob.progress_percent || 0}%` }}
                />
              </div>
              <div className="progress-stats">
                <span className="progress-percent">{batchJob.progress_percent?.toFixed(0) || 0}%</span>
                <span className="progress-count">
                  {batchJob.processed_items || 0} / {batchJob.total_items || 0} servers
                </span>
                {batchJob.success_count > 0 && (
                  <span className="progress-success">✓ {batchJob.success_count} success</span>
                )}
                {batchJob.failed_count > 0 && (
                  <span className="progress-failed">✗ {batchJob.failed_count} failed</span>
                )}
              </div>
              {isAdmin && batchJob.is_active && (
                <button 
                  className="cancel-btn" 
                  onClick={cancelBatchRefresh}
                >
                  Cancel Refresh
                </button>
              )}
            </>
          )}
          
          {!batchJob && (
            <p className="hint">Starting background refresh...</p>
          )}
        </div>
      ) : servers.length > 0 ? (
        <div className="server-grid">
          {servers.map((server) => (
            <ServerCard 
              key={server.id} 
              server={server} 
              onClick={() => handleServerClick(server)}
            />
          ))}
        </div>
      ) : (
        <div className="info-message">
          <p>No servers found in database.</p>
          {isAdmin ? (
            <p className="hint">Click "Discover Servers" to fetch servers from OVH API.</p>
          ) : (
            <p className="hint">Contact an administrator to discover servers.</p>
          )}
        </div>
      )}

      {/* Server Detail Modal */}
      {selectedServer && (
        <ServerDetailModal
          server={selectedServer}
          detail={serverDetail}
          loading={detailLoading}
          onClose={closeDetail}
          onRefreshMetrics={() => refreshServerMetrics(selectedServer.id)}
          isAdmin={isAdmin}
          onNavigateToVMs={handleNavigateToVMs}
        />
      )}
    </div>
  );
}

// =============================================================================
// Server Card Component
// =============================================================================

function ServerCard({ server, onClick }) {
  const hasAnomalies = server.has_anomalies || false;
  const monitoringEnabled = server.monitoring_enabled || false;
  const hasVMs = server.vm_count > 0;
  const runningVMs = server.running_vm_count || 0;
  const totalVMs = server.vm_count || 0;

  return (
    <div 
      className={`server-card ${hasAnomalies ? 'has-anomalies' : ''}`}
      onClick={onClick}
      style={{ cursor: 'pointer' }}
    >
      <div className="server-card-header">
        <h3>{server.server_name || server.name}</h3>
        <div className="server-badges">
          {hasAnomalies && <span className="badge badge-warning" title="Has interventions/anomalies">⚠</span>}
          {monitoringEnabled && <span className="badge badge-success" title="Monitoring enabled">📊</span>}
          {server.has_hypervisor && <span className="badge badge-hypervisor" title="Hypervisor configured">🖧</span>}
        </div>
      </div>
      
      <div className="server-card-status">
        <span className={`status-badge status-${server.status}`}>{server.status}</span>
      </div>
      
      <div className="server-card-info">
        <div className="info-row">
          <span className="label">IP:</span>
          <span className="value">{server.ip || 'N/A'}</span>
        </div>
        {server.provider && (
          <div className="info-row">
            <span className="label">Provider:</span>
            <span className="value">{server.provider.toUpperCase()}</span>
          </div>
        )}
        {server.location && (
          <div className="info-row">
            <span className="label">Location:</span>
            <span className="value">{server.location}</span>
          </div>
        )}
      </div>

      {/* VM Count Badge */}
      {hasVMs && (
        <div className="server-card-vms">
          <div className="vm-badge">
            <span className="vm-icon">🖥️</span>
            <span className="vm-count">
              <span className={`running ${runningVMs > 0 ? 'active' : ''}`}>{runningVMs}</span>
              <span className="separator">/</span>
              <span className="total">{totalVMs}</span>
            </span>
            <span className="vm-label">VMs</span>
          </div>
        </div>
      )}

      {/* Hardware Metrics */}
      {(server.cpu_cores > 0 || server.memory_gb > 0 || server.disk_total_gb > 0) && (
        <div className="server-card-metrics">
          <div className="metrics-title">Hardware</div>
          <div className="metrics-grid">
            {server.cpu_cores > 0 && (
              <div className="metric">
                <span className="metric-icon">🖥️</span>
                <span className="metric-value">{server.cpu_cores} cores</span>
              </div>
            )}
            {server.memory_gb > 0 && (
              <div className="metric">
                <span className="metric-icon">💾</span>
                <span className="metric-value">{server.memory_gb} GB RAM</span>
              </div>
            )}
            {server.disk_total_gb > 0 && (
              <div className="metric">
                <span className="metric-icon">💿</span>
                <span className="metric-value">{Math.round(server.disk_total_gb)} GB Disk</span>
              </div>
            )}
          </div>
        </div>
      )}

      <div className="server-card-footer">
        <span className="click-hint">Click for details →</span>
      </div>
    </div>
  );
}

// =============================================================================
// Server Detail Modal Component
// =============================================================================

function ServerDetailModal({ server, detail, loading, onClose, onRefreshMetrics, isAdmin, onNavigateToVMs }) {
  const metrics = detail?.latest_metrics;
  const [activeTab, setActiveTab] = useState('info');
  const vmSummary = detail?.vm_summary;
  const virtualMachines = detail?.virtual_machines || [];

  // Get VM state icon
  const getVmStateIcon = (state) => {
    switch (state) {
      case 'running': return '🟢';
      case 'stopped': return '⭕';
      case 'paused': return '⏸️';
      case 'suspended': return '💤';
      default: return '❓';
    }
  };

  // Get usage color class
  const getUsageClass = (percent) => {
    if (!percent) return '';
    if (percent >= 80) return 'usage-critical';
    if (percent >= 60) return 'usage-warning';
    return 'usage-normal';
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content server-detail-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{server.server_name || server.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        {vmSummary && (
          <div className="modal-tabs">
            <button 
              className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
              onClick={() => setActiveTab('info')}
            >
              Server Info
            </button>
            <button 
              className={`tab-btn ${activeTab === 'vms' ? 'active' : ''}`}
              onClick={() => setActiveTab('vms')}
            >
              Virtual Machines ({vmSummary.total})
            </button>
          </div>
        )}

        {loading ? (
          <div className="modal-body">
            <p>Loading server details...</p>
          </div>
        ) : detail ? (
          <div className="modal-body">
            {activeTab === 'info' && (
              <>
                {/* Status and Basic Info */}
                <section className="detail-section">
                  <h3>Server Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Status</span>
                      <span className={`status-badge status-${detail.status}`}>{detail.status}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Server ID</span>
                      <span className="detail-value">{detail.server_id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">IP Address</span>
                      <span className="detail-value">{detail.ip || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Provider</span>
                      <span className="detail-value">{detail.provider?.toUpperCase()}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Location</span>
                      <span className="detail-value">{detail.location || 'N/A'}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Hypervisor</span>
                      <span className="detail-value">{detail.hypervisor_type || 'N/A'}</span>
                    </div>
                  </div>
                </section>

                {/* VM Summary on Info Tab */}
                {vmSummary && (
                  <section className="detail-section vm-summary-section">
                    <h3>Virtual Machines Summary</h3>
                    <div className="vm-summary-grid">
                      <div className="vm-summary-item">
                        <span className="vm-summary-value">{vmSummary.total}</span>
                        <span className="vm-summary-label">Total VMs</span>
                      </div>
                      <div className="vm-summary-item running">
                        <span className="vm-summary-value">{vmSummary.running}</span>
                        <span className="vm-summary-label">Running</span>
                      </div>
                      <div className="vm-summary-item stopped">
                        <span className="vm-summary-value">{vmSummary.stopped}</span>
                        <span className="vm-summary-label">Stopped</span>
                      </div>
                      {vmSummary.resources && (
                        <>
                          <div className="vm-summary-item">
                            <span className="vm-summary-value">{vmSummary.resources.cpu_cores}</span>
                            <span className="vm-summary-label">vCPUs</span>
                          </div>
                          <div className="vm-summary-item">
                            <span className="vm-summary-value">{vmSummary.resources.memory_gb} GB</span>
                            <span className="vm-summary-label">Memory</span>
                          </div>
                        </>
                      )}
                    </div>
                    <button 
                      className="btn-link view-vms-link"
                      onClick={() => setActiveTab('vms')}
                    >
                      View all VMs →
                    </button>
                  </section>
                )}

                {/* Hardware Specifications */}
                {metrics && (
                  <section className="detail-section">
                    <h3>Hardware Specifications</h3>
                    <div className="detail-grid">
                      {metrics.cpu_cores && (
                        <div className="detail-item">
                          <span className="detail-label">CPU Cores</span>
                          <span className="detail-value">{metrics.cpu_cores}</span>
                        </div>
                      )}
                      {metrics.cpu_frequency && (
                        <div className="detail-item">
                          <span className="detail-label">CPU Frequency</span>
                          <span className="detail-value">{metrics.cpu_frequency} GHz</span>
                        </div>
                      )}
                      {metrics.memory_gb && (
                        <div className="detail-item">
                          <span className="detail-label">Memory</span>
                          <span className="detail-value">{metrics.memory_gb} GB</span>
                        </div>
                      )}
                      {metrics.disk_total_gb && (
                        <div className="detail-item">
                          <span className="detail-label">Total Disk</span>
                          <span className="detail-value">{Math.round(metrics.disk_total_gb)} GB</span>
                        </div>
                      )}
                      {metrics.disk_count && (
                        <div className="detail-item">
                          <span className="detail-label">Disk Count</span>
                          <span className="detail-value">{metrics.disk_count}</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Network Interfaces */}
                {metrics?.network_interfaces?.length > 0 && (
                  <section className="detail-section">
                    <h3>Network Interfaces</h3>
                    <div className="network-list">
                      {metrics.network_interfaces.map((iface, idx) => (
                        <div key={idx} className="network-item">
                          <span className="network-name">{iface.name}</span>
                          {iface.mac && <span className="network-mac">MAC: {iface.mac}</span>}
                          {iface.speed && <span className="network-speed">{iface.speed} Mbps</span>}
                          {iface.link_type && <span className="network-type">{iface.link_type}</span>}
                        </div>
                      ))}
                    </div>
                  </section>
                )}

                {/* Monitoring Status */}
                {metrics && (
                  <section className="detail-section">
                    <h3>Monitoring & Service</h3>
                    <div className="detail-grid">
                      <div className="detail-item">
                        <span className="detail-label">Monitoring</span>
                        <span className={`detail-value ${metrics.monitoring_enabled ? 'success' : 'warning'}`}>
                          {metrics.monitoring_enabled ? '✓ Enabled' : '⚠ Disabled'}
                        </span>
                      </div>
                      {metrics.monitoring_state && (
                        <div className="detail-item">
                          <span className="detail-label">State</span>
                          <span className="detail-value">{metrics.monitoring_state}</span>
                        </div>
                      )}
                      {metrics.service_status && (
                        <div className="detail-item">
                          <span className="detail-label">Service Status</span>
                          <span className="detail-value">{metrics.service_status}</span>
                        </div>
                      )}
                      {metrics.support_level && (
                        <div className="detail-item">
                          <span className="detail-label">Support Level</span>
                          <span className="detail-value">{metrics.support_level}</span>
                        </div>
                      )}
                      {metrics.service_expiration && (
                        <div className="detail-item">
                          <span className="detail-label">Expiration</span>
                          <span className="detail-value">{metrics.service_expiration}</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Anomalies / Interventions */}
                {metrics && metrics.intervention_count > 0 && (
                  <section className="detail-section anomalies-section">
                    <h3>⚠️ Interventions / Anomalies</h3>
                    <div className="anomaly-alert">
                      <p>This server has {metrics.intervention_count} recorded intervention(s).</p>
                      {metrics.latest_intervention?.date && (
                        <p className="anomaly-detail">
                          Latest: {metrics.latest_intervention.type || 'Intervention'} on {metrics.latest_intervention.date}
                        </p>
                      )}
                    </div>
                  </section>
                )}

                {/* Days until expiration warning */}
                {detail.days_until_expiration !== null && detail.days_until_expiration <= 30 && (
                  <section className="detail-section expiration-warning">
                    <h3>⚠️ Service Expiration Warning</h3>
                    <div className="expiration-alert">
                      <p>This server's service expires in <strong>{detail.days_until_expiration} days</strong>.</p>
                    </div>
                  </section>
                )}

                {/* Timestamps */}
                <section className="detail-section">
                  <h3>Timestamps</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Last Discovered</span>
                      <span className="detail-value">
                        {detail.last_discovered ? new Date(detail.last_discovered).toLocaleString() : 'Never'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Last Updated</span>
                      <span className="detail-value">
                        {detail.updated_at ? new Date(detail.updated_at).toLocaleString() : 'N/A'}
                      </span>
                    </div>
                    {metrics?.collected_at && (
                      <div className="detail-item">
                        <span className="detail-label">Metrics Updated</span>
                        <span className="detail-value">
                          {new Date(metrics.collected_at).toLocaleString()}
                        </span>
                      </div>
                    )}
                  </div>
                </section>
              </>
            )}

            {activeTab === 'vms' && (
              <div className="vms-tab-content">
                {/* VM List Header */}
                <div className="vms-tab-header">
                  <span className="vms-count">
                    {vmSummary?.running || 0} running / {vmSummary?.total || 0} total
                  </span>
                  {onNavigateToVMs && (
                    <button 
                      className="btn-link"
                      onClick={() => {
                        onClose();
                        onNavigateToVMs(detail.id);
                      }}
                    >
                      View in VM List →
                    </button>
                  )}
                </div>

                {/* VM Cards */}
                {virtualMachines.length > 0 ? (
                  <div className="server-vm-list">
                    {virtualMachines.map((vm) => (
                      <div key={vm.id} className={`server-vm-item state-${vm.state}`}>
                        <div className="server-vm-header">
                          <span className="server-vm-state">{getVmStateIcon(vm.state)}</span>
                          <span className="server-vm-name">{vm.name}</span>
                          <span className={`hypervisor-badge ${vm.hypervisor_type}`}>
                            {vm.hypervisor_type === 'proxmox' ? 'Proxmox' : 'Hyper-V'}
                          </span>
                        </div>
                        <div className="server-vm-details">
                          <span className="server-vm-resources">
                            {vm.cpu_cores} vCPU • {vm.memory_gb || Math.round((vm.memory_mb || 0) / 1024)} GB RAM
                          </span>
                          {vm.ip_addresses?.length > 0 && (
                            <span className="server-vm-ip">{vm.ip_addresses[0]}</span>
                          )}
                        </div>
                        {vm.state === 'running' && (vm.cpu_usage_percent || vm.memory_usage_percent) && (
                          <div className="server-vm-usage">
                            {vm.cpu_usage_percent !== null && (
                              <div className="usage-item">
                                <span className="usage-label">CPU</span>
                                <div className="usage-bar">
                                  <div 
                                    className={`usage-fill ${getUsageClass(vm.cpu_usage_percent)}`}
                                    style={{ width: `${vm.cpu_usage_percent}%` }}
                                  />
                                </div>
                                <span className="usage-value">{Math.round(vm.cpu_usage_percent)}%</span>
                              </div>
                            )}
                            {vm.memory_usage_percent !== null && (
                              <div className="usage-item">
                                <span className="usage-label">Mem</span>
                                <div className="usage-bar">
                                  <div 
                                    className={`usage-fill ${getUsageClass(vm.memory_usage_percent)}`}
                                    style={{ width: `${vm.memory_usage_percent}%` }}
                                  />
                                </div>
                                <span className="usage-value">{Math.round(vm.memory_usage_percent)}%</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-vms">
                    <p>No virtual machines found on this server.</p>
                  </div>
                )}
              </div>
            )}
          </div>
        ) : (
          <div className="modal-body">
            <p>Failed to load server details.</p>
          </div>
        )}

        <div className="modal-footer">
          {isAdmin && detail?.provider === 'ovh' && (
            <button 
              onClick={onRefreshMetrics} 
              disabled={loading}
              className="metrics-btn"
            >
              {loading ? 'Refreshing...' : '↻ Refresh Metrics'}
            </button>
          )}
          <button onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// Alerts Component
// =============================================================================

function Alerts() {
  const [alertStats, setAlertStats] = useState(null);
  const [alertHistory, setAlertHistory] = useState([]);
  const [alertRules, setAlertRules] = useState([]);
  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  const [showRuleModal, setShowRuleModal] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  
  // Channel form state
  const [channelForm, setChannelForm] = useState({
    name: '',
    channel_type: 'slack',
    slack_webhook_url: '',
    slack_channel: '',
    email_addresses: '',
    enabled: true,
  });
  
  // Rule form state
  const [ruleForm, setRuleForm] = useState({
    name: '',
    description: '',
    alert_type: 'intervention',
    severity: 'warning',
    apply_to_all_servers: true,
    target_providers: [],
    notification_channels: [],
    cooldown_minutes: 60,
    enabled: true,
    condition_config: {},
  });
  
  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      fetchAlertData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated]);

  const fetchAlertData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [statsRes, historyRes, rulesRes, channelsRes] = await Promise.all([
        api.get('/api/alerts/stats/'),
        api.get('/api/alert-history/'),
        isAdmin ? api.get('/api/alert-rules/') : Promise.resolve({ data: { results: [] } }),
        isAdmin ? api.get('/api/notification-channels/') : Promise.resolve({ data: { results: [] } }),
      ]);
      setAlertStats(statsRes.data);
      setAlertHistory(historyRes.data.results || []);
      setAlertRules(rulesRes.data.results || []);
      setChannels(channelsRes.data.results || []);
    } catch (err) {
      console.error('Failed to fetch alert data:', err);
      setError('Failed to load alert data');
    } finally {
      setLoading(false);
    }
  };

  const acknowledgeAlert = async (alertId) => {
    try {
      await api.post(`/api/alert-history/${alertId}/acknowledge/`);
      fetchAlertData();
    } catch (err) {
      console.error('Failed to acknowledge alert:', err);
    }
  };

  const resolveAlert = async (alertId, notes = '') => {
    try {
      await api.post(`/api/alert-history/${alertId}/resolve/`, { resolution_notes: notes });
      fetchAlertData();
    } catch (err) {
      console.error('Failed to resolve alert:', err);
    }
  };

  const toggleRule = async (ruleId) => {
    try {
      await api.post(`/api/alert-rules/${ruleId}/toggle/`);
      fetchAlertData();
    } catch (err) {
      console.error('Failed to toggle rule:', err);
    }
  };

  const testChannel = async (channelId) => {
    try {
      await api.post(`/api/notification-channels/${channelId}/test/`);
      alert('Test notification sent successfully!');
    } catch (err) {
      alert('Test notification failed: ' + (err.response?.data?.details || err.message));
    }
  };

  const [evaluating, setEvaluating] = useState(false);

  const evaluateRules = async () => {
    try {
      setEvaluating(true);
      const response = await api.post('/api/alert-rules/evaluate/');
      const result = response.data.data;
      const count = result?.triggered_count || 0;
      
      if (count > 0) {
        alert(`✅ Evaluation complete!\n\n${count} alert(s) triggered.\n\nCheck the History tab for details.`);
      } else {
        alert(`✅ Evaluation complete!\n\nNo alerts triggered.\nAll systems operating within normal parameters.`);
      }
      fetchAlertData();
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      if (err.response?.status === 429) {
        alert('⏱️ Rate limit exceeded.\n\nPlease wait 60 seconds before evaluating again.');
      } else {
        alert('❌ Evaluation failed:\n\n' + errorMsg);
      }
    } finally {
      setEvaluating(false);
    }
  };

  // Reset channel form
  const resetChannelForm = () => {
    setChannelForm({
      name: '',
      channel_type: 'slack',
      slack_webhook_url: '',
      slack_channel: '',
      email_addresses: '',
      enabled: true,
    });
    setEditingChannel(null);
    setFormError(null);
  };

  // Reset rule form
  const resetRuleForm = () => {
    setRuleForm({
      name: '',
      description: '',
      alert_type: 'intervention',
      severity: 'warning',
      apply_to_all_servers: true,
      target_providers: [],
      notification_channels: [],
      cooldown_minutes: 60,
      enabled: true,
      condition_config: {},
    });
    setEditingRule(null);
    setFormError(null);
  };

  // Check if alert type needs threshold config
  const needsThreshold = ['high_cpu', 'high_memory', 'low_disk', 'service_expiring'].includes(ruleForm.alert_type);
  
  // Get default threshold based on alert type
  const getDefaultThreshold = (alertType) => {
    switch (alertType) {
      case 'high_cpu': return 90;
      case 'high_memory': return 90;
      case 'low_disk': return 10;
      case 'service_expiring': return 30;
      default: return 90;
    }
  };
  
  // Get threshold label based on alert type
  const getThresholdLabel = (alertType) => {
    switch (alertType) {
      case 'high_cpu': return 'CPU Usage Threshold (%)';
      case 'high_memory': return 'Memory Usage Threshold (%)';
      case 'low_disk': return 'Minimum Free Disk Space (%)';
      case 'service_expiring': return 'Days Before Expiration';
      default: return 'Threshold';
    }
  };

  // Create/Update notification channel
  const saveChannel = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        name: channelForm.name,
        channel_type: channelForm.channel_type,
        enabled: channelForm.enabled,
      };

      if (channelForm.channel_type === 'slack') {
        payload.slack_webhook_url = channelForm.slack_webhook_url;
        payload.slack_channel = channelForm.slack_channel || null;
      } else if (channelForm.channel_type === 'email') {
        // Parse comma-separated emails
        payload.email_addresses = channelForm.email_addresses
          .split(',')
          .map(e => e.trim())
          .filter(e => e.length > 0);
      }

      if (editingChannel) {
        await api.put(`/api/notification-channels/${editingChannel.id}/`, payload);
      } else {
        await api.post('/api/notification-channels/', payload);
      }

      setShowChannelModal(false);
      resetChannelForm();
      fetchAlertData();
    } catch (err) {
      console.error('Failed to save channel:', err);
      const errorMsg = err.response?.data?.slack_webhook_url?.[0] ||
                       err.response?.data?.email_addresses?.[0] ||
                       err.response?.data?.name?.[0] ||
                       err.response?.data?.error ||
                       err.response?.data?.detail ||
                       'Failed to save channel';
      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  // Create/Update alert rule
  const saveRule = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        name: ruleForm.name,
        description: ruleForm.description || '',
        alert_type: ruleForm.alert_type,
        severity: ruleForm.severity,
        apply_to_all_servers: ruleForm.apply_to_all_servers,
        target_providers: ruleForm.target_providers,
        notification_channels: ruleForm.notification_channels,
        cooldown_minutes: parseInt(ruleForm.cooldown_minutes) || 60,
        enabled: ruleForm.enabled,
        condition_config: ruleForm.condition_config || {},
      };

      if (editingRule) {
        await api.put(`/api/alert-rules/${editingRule.id}/`, payload);
      } else {
        await api.post('/api/alert-rules/', payload);
      }

      setShowRuleModal(false);
      resetRuleForm();
      fetchAlertData();
    } catch (err) {
      console.error('Failed to save rule:', err);
      const errorMsg = err.response?.data?.name?.[0] ||
                       err.response?.data?.notification_channels?.[0] ||
                       err.response?.data?.error ||
                       err.response?.data?.detail ||
                       'Failed to save rule';
      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete channel
  const deleteChannel = async (channelId) => {
    if (!confirm('Are you sure you want to delete this notification channel?')) return;
    try {
      await api.delete(`/api/notification-channels/${channelId}/`);
      fetchAlertData();
    } catch (err) {
      alert('Failed to delete channel: ' + (err.response?.data?.error || err.message));
    }
  };

  // Delete rule
  const deleteRule = async (ruleId) => {
    if (!confirm('Are you sure you want to delete this alert rule?')) return;
    try {
      await api.delete(`/api/alert-rules/${ruleId}/`);
      fetchAlertData();
    } catch (err) {
      alert('Failed to delete rule: ' + (err.response?.data?.error || err.message));
    }
  };

  // Open channel modal for editing
  const openEditChannel = (channel) => {
    setChannelForm({
      name: channel.name,
      channel_type: channel.channel_type,
      slack_webhook_url: '',  // Don't prefill webhook for security
      slack_channel: channel.slack_channel || '',
      email_addresses: (channel.email_addresses || []).join(', '),
      enabled: channel.enabled,
    });
    setEditingChannel(channel);
    setFormError(null);
    setShowChannelModal(true);
  };

  // Open rule modal for editing
  const openEditRule = (rule) => {
    setRuleForm({
      name: rule.name,
      description: rule.description || '',
      alert_type: rule.alert_type,
      severity: rule.severity,
      apply_to_all_servers: rule.apply_to_all_servers,
      target_providers: rule.target_providers || [],
      notification_channels: rule.notification_channels || [],
      cooldown_minutes: rule.cooldown_minutes || 60,
      enabled: rule.enabled,
      condition_config: rule.condition_config || {},
    });
    setEditingRule(rule);
    setFormError(null);
    setShowRuleModal(true);
  };

  if (!isAuthenticated) {
    return (
      <div className="alerts">
        <h2>🔔 Alerts</h2>
        <div className="auth-notice">
          <p>🔒 <Link to="/login">Sign in</Link> to view alerts and configure notifications.</p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="alerts">
        <h2>🔔 Alerts</h2>
        <p>Loading alert data...</p>
      </div>
    );
  }

  return (
    <div className="alerts">
      <h2>🔔 Alerts</h2>
      
      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div className="alert-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          Overview
        </button>
        <button 
          className={`tab-btn ${activeTab === 'history' ? 'active' : ''}`}
          onClick={() => setActiveTab('history')}
        >
          Alert History
        </button>
        {isAdmin && (
          <>
            <button 
              className={`tab-btn ${activeTab === 'rules' ? 'active' : ''}`}
              onClick={() => setActiveTab('rules')}
            >
              Alert Rules
            </button>
            <button 
              className={`tab-btn ${activeTab === 'channels' ? 'active' : ''}`}
              onClick={() => setActiveTab('channels')}
            >
              Notification Channels
            </button>
          </>
        )}
      </div>

      {/* Overview Tab */}
      {activeTab === 'overview' && alertStats && (
        <div className="alert-overview">
          <div className="alert-stats-grid">
            <div className="alert-stat-card">
              <span className="stat-value">{alertStats.total_alerts}</span>
              <span className="stat-label">Total Alerts</span>
            </div>
            <div className="alert-stat-card critical">
              <span className="stat-value">{alertStats.triggered_alerts}</span>
              <span className="stat-label">Active</span>
            </div>
            <div className="alert-stat-card warning">
              <span className="stat-value">{alertStats.acknowledged_alerts}</span>
              <span className="stat-label">Acknowledged</span>
            </div>
            <div className="alert-stat-card success">
              <span className="stat-value">{alertStats.resolved_alerts}</span>
              <span className="stat-label">Resolved</span>
            </div>
          </div>

          <div className="alert-summary-row">
            <div className="summary-card">
              <h4>By Severity</h4>
              <div className="severity-list">
                <div className="severity-item critical">
                  <span>🚨 Critical</span>
                  <span>{alertStats.alerts_by_severity?.critical || 0}</span>
                </div>
                <div className="severity-item warning">
                  <span>⚠️ Warning</span>
                  <span>{alertStats.alerts_by_severity?.warning || 0}</span>
                </div>
                <div className="severity-item info">
                  <span>ℹ️ Info</span>
                  <span>{alertStats.alerts_by_severity?.info || 0}</span>
                </div>
              </div>
            </div>

            <div className="summary-card">
              <h4>Alert Rules</h4>
              <div className="rules-summary">
                <p><strong>{alertStats.active_rules}</strong> active rules</p>
                <p>{alertStats.total_rules} total rules</p>
                <div className="evaluation-status">
                  {alertStats.background_enabled ? (
                    <span className="badge badge-success" title={`Automatic checks every ${alertStats.evaluation_interval_minutes || 5} minutes`}>
                      ✓ Auto-check enabled
                    </span>
                  ) : (
                    <span className="badge badge-muted" title="Background evaluation not configured. Use 'Evaluate Now' for manual checks.">
                      Manual checks only
                    </span>
                  )}
                </div>
                {alertStats.last_evaluation && (
                  <p className="last-evaluation">
                    Last check: {new Date(alertStats.last_evaluation).toLocaleString()}
                  </p>
                )}
                {alertStats.background_enabled && alertStats.next_evaluation && (
                  <p className="next-evaluation">
                    Next check: ~{new Date(alertStats.next_evaluation).toLocaleTimeString()}
                  </p>
                )}
                {isAdmin && (
                  <button 
                    onClick={evaluateRules} 
                    className={`evaluate-btn ${evaluating ? 'loading' : ''}`}
                    disabled={evaluating}
                  >
                    {evaluating ? '⏳ Evaluating...' : '🔄 Evaluate Now'}
                  </button>
                )}
              </div>
            </div>
          </div>

          {alertStats.recent_alerts?.length > 0 && (
            <div className="recent-alerts-section">
              <h4>Recent Alerts</h4>
              <div className="recent-alerts-list">
                {alertStats.recent_alerts.slice(0, 5).map(alert => (
                  <div key={alert.id} className={`recent-alert-item ${alert.severity}`}>
                    <span className="alert-severity-badge">{alert.severity}</span>
                    <span className="alert-message">{alert.message}</span>
                    <span className="alert-server">{alert.server_name}</span>
                    <span className="alert-time">
                      {new Date(alert.triggered_at).toLocaleString()}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="alert-history-section">
          {alertHistory.length === 0 ? (
            <p className="no-alerts">No alerts recorded yet.</p>
          ) : (
            <table className="alert-history-table">
              <thead>
                <tr>
                  <th>Severity</th>
                  <th>Alert</th>
                  <th>Server</th>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alertHistory.map(alert => (
                  <tr key={alert.id} className={`severity-${alert.severity}`}>
                    <td>
                      <span className={`severity-badge ${alert.severity}`}>
                        {alert.severity === 'critical' && '🚨'}
                        {alert.severity === 'warning' && '⚠️'}
                        {alert.severity === 'info' && 'ℹ️'}
                        {alert.severity}
                      </span>
                    </td>
                    <td>{alert.message}</td>
                    <td>{alert.server_name}</td>
                    <td>{new Date(alert.triggered_at).toLocaleString()}</td>
                    <td>
                      <span className={`status-badge ${alert.status}`}>
                        {alert.status}
                      </span>
                    </td>
                    <td>
                      {alert.status === 'triggered' && (
                        <button 
                          onClick={() => acknowledgeAlert(alert.id)}
                          className="action-btn acknowledge"
                        >
                          Acknowledge
                        </button>
                      )}
                      {alert.status !== 'resolved' && (
                        <button 
                          onClick={() => resolveAlert(alert.id)}
                          className="action-btn resolve"
                        >
                          Resolve
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Rules Tab (Admin Only) */}
      {activeTab === 'rules' && isAdmin && (
        <div className="alert-rules-section">
          <div className="section-header">
            <h3>Alert Rules</h3>
            <button onClick={() => { resetRuleForm(); setShowRuleModal(true); }} className="add-btn">
              + Add Rule
            </button>
          </div>

          {alertRules.length === 0 ? (
            <p className="no-rules">No alert rules configured. Create your first rule to start receiving notifications.</p>
          ) : (
            <table className="rules-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Type</th>
                  <th>Severity</th>
                  <th>Channels</th>
                  <th>Triggers</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {alertRules.map(rule => (
                  <tr key={rule.id}>
                    <td>{rule.name}</td>
                    <td>{rule.alert_type}</td>
                    <td>
                      <span className={`severity-badge ${rule.severity}`}>
                        {rule.severity}
                      </span>
                    </td>
                    <td>{rule.notification_channel_count || 0}</td>
                    <td>{rule.trigger_count}</td>
                    <td>
                      <span className={`status-indicator ${rule.enabled ? 'enabled' : 'disabled'}`}>
                        {rule.enabled ? 'Enabled' : 'Disabled'}
                      </span>
                    </td>
                    <td>
                      <div className="rule-actions">
                        <button 
                          onClick={() => toggleRule(rule.id)}
                          className={`action-btn ${rule.enabled ? 'disable' : 'enable'}`}
                        >
                          {rule.enabled ? 'Disable' : 'Enable'}
                        </button>
                        <button onClick={() => openEditRule(rule)} className="edit-btn small">
                          Edit
                        </button>
                        <button onClick={() => deleteRule(rule.id)} className="delete-btn small">
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Channels Tab (Admin Only) */}
      {activeTab === 'channels' && isAdmin && (
        <div className="notification-channels-section">
          <div className="section-header">
            <h3>Notification Channels</h3>
            <button onClick={() => { resetChannelForm(); setShowChannelModal(true); }} className="add-btn">
              + Add Channel
            </button>
          </div>

          {channels.length === 0 ? (
            <p className="no-channels">No notification channels configured. Add Slack or Email channels to receive alerts.</p>
          ) : (
            <div className="channels-grid">
              {channels.map(channel => (
                <div key={channel.id} className={`channel-card ${channel.channel_type}`}>
                  <div className="channel-header">
                    <span className="channel-icon">
                      {channel.channel_type === 'slack' && '💬'}
                      {channel.channel_type === 'email' && '📧'}
                    </span>
                    <h4>{channel.name}</h4>
                    {channel.verified && <span className="verified-badge">✓ Verified</span>}
                  </div>
                  <div className="channel-type">{channel.channel_type.toUpperCase()}</div>
                  <div className="channel-status">
                    <span className={channel.enabled ? 'enabled' : 'disabled'}>
                      {channel.enabled ? 'Enabled' : 'Disabled'}
                    </span>
                    {channel.last_used && (
                      <span className="last-used">
                        Last used: {new Date(channel.last_used).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <div className="channel-actions">
                    <button onClick={() => testChannel(channel.id)} className="test-btn">
                      Test
                    </button>
                    <button onClick={() => openEditChannel(channel)} className="edit-btn">
                      Edit
                    </button>
                    <button onClick={() => deleteChannel(channel.id)} className="delete-btn">
                      Delete
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Add/Edit Channel Modal */}
      {showChannelModal && (
        <div className="modal-overlay" onClick={() => setShowChannelModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingChannel ? 'Edit Channel' : 'Add Notification Channel'}</h3>
              <button className="close-btn" onClick={() => setShowChannelModal(false)}>×</button>
            </div>
            <form onSubmit={saveChannel}>
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="form-group">
                <label>Channel Name *</label>
                <input
                  type="text"
                  value={channelForm.name}
                  onChange={e => setChannelForm({...channelForm, name: e.target.value})}
                  placeholder="e.g., Ops Team Slack"
                  required
                />
              </div>

              <div className="form-group">
                <label>Channel Type *</label>
                <select
                  value={channelForm.channel_type}
                  onChange={e => setChannelForm({...channelForm, channel_type: e.target.value})}
                >
                  <option value="slack">Slack</option>
                  <option value="email">Email</option>
                </select>
              </div>

              {channelForm.channel_type === 'slack' && (
                <>
                  <div className="form-group">
                    <label>Webhook URL *</label>
                    <input
                      type="url"
                      value={channelForm.slack_webhook_url}
                      onChange={e => setChannelForm({...channelForm, slack_webhook_url: e.target.value})}
                      placeholder="https://hooks.slack.com/services/..."
                      required={!editingChannel}
                    />
                    {editingChannel && <small>Leave blank to keep existing webhook</small>}
                  </div>
                  <div className="form-group">
                    <label>Channel Override (optional)</label>
                    <input
                      type="text"
                      value={channelForm.slack_channel}
                      onChange={e => setChannelForm({...channelForm, slack_channel: e.target.value})}
                      placeholder="#alerts"
                    />
                  </div>
                </>
              )}

              {channelForm.channel_type === 'email' && (
                <div className="form-group">
                  <label>Email Addresses * (comma-separated)</label>
                  <textarea
                    value={channelForm.email_addresses}
                    onChange={e => setChannelForm({...channelForm, email_addresses: e.target.value})}
                    placeholder="ops@example.com, admin@example.com"
                    rows={3}
                    required
                  />
                </div>
              )}

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={channelForm.enabled}
                    onChange={e => setChannelForm({...channelForm, enabled: e.target.checked})}
                  />
                  Enabled
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowChannelModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={formLoading} className="save-btn">
                  {formLoading ? 'Saving...' : (editingChannel ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Add/Edit Rule Modal */}
      {showRuleModal && (
        <div className="modal-overlay" onClick={() => setShowRuleModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingRule ? 'Edit Alert Rule' : 'Add Alert Rule'}</h3>
              <button className="close-btn" onClick={() => setShowRuleModal(false)}>×</button>
            </div>
            <form onSubmit={saveRule}>
              {formError && <div className="form-error">{formError}</div>}
              
              <div className="form-group">
                <label>Rule Name *</label>
                <input
                  type="text"
                  value={ruleForm.name}
                  onChange={e => setRuleForm({...ruleForm, name: e.target.value})}
                  placeholder="e.g., Critical Intervention Alert"
                  required
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={ruleForm.description}
                  onChange={e => setRuleForm({...ruleForm, description: e.target.value})}
                  placeholder="Optional description of this alert rule"
                  rows={2}
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Alert Type *</label>
                  <select
                    value={ruleForm.alert_type}
                    onChange={e => setRuleForm({...ruleForm, alert_type: e.target.value})}
                  >
                    <option value="intervention">Intervention/Anomaly</option>
                    <option value="service_expiring">Service Expiring</option>
                    <option value="server_down">Server Down</option>
                    <option value="high_cpu">High CPU Usage</option>
                    <option value="high_memory">High Memory Usage</option>
                    <option value="low_disk">Low Disk Space</option>
                    <option value="high_error_count">High Error Count</option>
                    <option value="monitoring_disabled">Monitoring Disabled</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Severity *</label>
                  <select
                    value={ruleForm.severity}
                    onChange={e => setRuleForm({...ruleForm, severity: e.target.value})}
                  >
                    <option value="critical">Critical</option>
                    <option value="warning">Warning</option>
                    <option value="info">Info</option>
                  </select>
                </div>
              </div>

              {needsThreshold && (
                <div className="form-group">
                  <label>{getThresholdLabel(ruleForm.alert_type)}</label>
                  <input
                    type="number"
                    min="1"
                    max={ruleForm.alert_type === 'service_expiring' ? 365 : 100}
                    value={ruleForm.condition_config?.threshold_percent || 
                           ruleForm.condition_config?.threshold_days || 
                           getDefaultThreshold(ruleForm.alert_type)}
                    onChange={e => {
                      const key = ruleForm.alert_type === 'service_expiring' ? 'threshold_days' : 'threshold_percent';
                      setRuleForm({
                        ...ruleForm,
                        condition_config: { ...ruleForm.condition_config, [key]: parseInt(e.target.value) || 0 }
                      });
                    }}
                  />
                  <small>
                    {ruleForm.alert_type === 'high_cpu' && 'Alert when CPU usage exceeds this percentage'}
                    {ruleForm.alert_type === 'high_memory' && 'Alert when memory usage exceeds this percentage'}
                    {ruleForm.alert_type === 'low_disk' && 'Alert when free disk space falls below this percentage'}
                    {ruleForm.alert_type === 'service_expiring' && 'Alert when service expires within this many days'}
                  </small>
                </div>
              )}

              <div className="form-group">
                <label>Notification Channels</label>
                <select
                  multiple
                  value={ruleForm.notification_channels}
                  onChange={e => setRuleForm({
                    ...ruleForm, 
                    notification_channels: Array.from(e.target.selectedOptions, opt => opt.value)
                  })}
                  size={Math.min(channels.length + 1, 4)}
                >
                  {channels.map(ch => (
                    <option key={ch.id} value={ch.id}>
                      {ch.name} ({ch.channel_type})
                    </option>
                  ))}
                </select>
                <small>Hold Ctrl/Cmd to select multiple. Create channels first if none exist.</small>
              </div>

              <div className="form-group">
                <label>Cooldown (minutes)</label>
                <input
                  type="number"
                  min="1"
                  max="1440"
                  value={ruleForm.cooldown_minutes}
                  onChange={e => setRuleForm({...ruleForm, cooldown_minutes: e.target.value})}
                />
                <small>Minimum time between repeated alerts for the same condition</small>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={ruleForm.apply_to_all_servers}
                    onChange={e => setRuleForm({...ruleForm, apply_to_all_servers: e.target.checked})}
                  />
                  Apply to all servers
                </label>
              </div>

              <div className="form-group checkbox">
                <label>
                  <input
                    type="checkbox"
                    checked={ruleForm.enabled}
                    onChange={e => setRuleForm({...ruleForm, enabled: e.target.checked})}
                  />
                  Enabled
                </label>
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowRuleModal(false)} className="cancel-btn">
                  Cancel
                </button>
                <button type="submit" disabled={formLoading} className="save-btn">
                  {formLoading ? 'Saving...' : (editingRule ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Hypervisors Management Component (Admin Only)
// =============================================================================

function Hypervisors() {
  const [hypervisors, setHypervisors] = useState([]);
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('list');
  const [showModal, setShowModal] = useState(false);
  const [editingHypervisor, setEditingHypervisor] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState(null);
  const [testingId, setTestingId] = useState(null);
  const [testResult, setTestResult] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const [syncResult, setSyncResult] = useState(null);
  const [stats, setStats] = useState(null);

  // Form state
  const [form, setForm] = useState({
    server: '',
    hypervisor_type: 'proxmox',
    display_name: '',
    // Proxmox fields
    proxmox_host: '',
    proxmox_port: 8006,
    proxmox_node: '',
    proxmox_token_id: '',
    proxmox_token_secret: '',
    proxmox_verify_ssl: true,
    // Hyper-V fields
    hyperv_host: '',
    hyperv_port: 5986,
    hyperv_username: '',
    hyperv_password: '',
    hyperv_use_ssl: true,
    hyperv_verify_ssl: false,
    // Sync config
    sync_enabled: true,
    sync_interval_minutes: 15,
  });

  const { isAuthenticated, isAdmin } = useAuth();

  useEffect(() => {
    if (isAuthenticated && isAdmin) {
      fetchData();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, isAdmin]);

  const fetchData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [hypervisorsRes, serversRes, statsRes] = await Promise.all([
        api.get('/api/hypervisors/'),
        api.get('/api/servers/?page_size=100'),
        api.get('/api/hypervisors/stats/').catch(() => ({ data: null })),
      ]);
      
      // Handle hypervisors response
      const hypervisorsList = hypervisorsRes.data?.results || hypervisorsRes.data || [];
      setHypervisors(Array.isArray(hypervisorsList) ? hypervisorsList : []);
      
      // Handle servers response - check both paginated and non-paginated formats
      const serversList = serversRes.data?.results || serversRes.data || [];
      const validServers = Array.isArray(serversList) ? serversList : [];
      setServers(validServers);
      
      // Debug log if servers empty but shouldn't be
      if (validServers.length === 0) {
        console.warn('No servers returned from API. Response:', serversRes.data);
      }
      
      setStats(statsRes.data);
    } catch (err) {
      console.error('Failed to fetch hypervisor data:', err);
      setError('Failed to load hypervisor data. Please try refreshing the page.');
    } finally {
      setLoading(false);
    }
  };

  // Reset form to defaults
  const resetForm = () => {
    setForm({
      server: '',
      hypervisor_type: 'proxmox',
      display_name: '',
      proxmox_host: '',
      proxmox_port: 8006,
      proxmox_node: '',
      proxmox_token_id: '',
      proxmox_token_secret: '',
      proxmox_verify_ssl: true,
      hyperv_host: '',
      hyperv_port: 5986,
      hyperv_username: '',
      hyperv_password: '',
      hyperv_use_ssl: true,
      hyperv_verify_ssl: false,
      sync_enabled: true,
      sync_interval_minutes: 15,
    });
    setEditingHypervisor(null);
    setFormError(null);
  };

  // Open modal for new hypervisor
  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  // Open modal for editing
  const openEditModal = (hv) => {
    setForm({
      server: hv.server?.id || hv.server,
      hypervisor_type: hv.hypervisor_type,
      display_name: hv.display_name || '',
      proxmox_host: hv.proxmox_host || '',
      proxmox_port: hv.proxmox_port || 8006,
      proxmox_node: hv.proxmox_node || '',
      proxmox_token_id: hv.proxmox_token_id || '',
      proxmox_token_secret: '', // Don't prefill secrets
      proxmox_verify_ssl: hv.proxmox_verify_ssl ?? true,
      hyperv_host: hv.hyperv_host || '',
      hyperv_port: hv.hyperv_port || 5986,
      hyperv_username: hv.hyperv_username || '',
      hyperv_password: '', // Don't prefill secrets
      hyperv_use_ssl: hv.hyperv_use_ssl ?? true,
      hyperv_verify_ssl: hv.hyperv_verify_ssl ?? false,
      sync_enabled: hv.sync_enabled ?? true,
      sync_interval_minutes: hv.sync_interval_minutes || 15,
    });
    setEditingHypervisor(hv);
    setFormError(null);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    resetForm();
  };

  // Save hypervisor (create or update)
  const saveHypervisor = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    setFormError(null);

    try {
      const payload = {
        hypervisor_type: form.hypervisor_type,
        display_name: form.display_name || null,
        sync_enabled: form.sync_enabled,
        sync_interval_minutes: parseInt(form.sync_interval_minutes) || 15,
      };

      // Only include server when creating
      if (!editingHypervisor) {
        payload.server = form.server;
      }

      // Add type-specific fields
      if (form.hypervisor_type === 'proxmox') {
        payload.proxmox_host = form.proxmox_host;
        payload.proxmox_port = parseInt(form.proxmox_port) || 8006;
        payload.proxmox_node = form.proxmox_node;
        payload.proxmox_token_id = form.proxmox_token_id;
        payload.proxmox_verify_ssl = form.proxmox_verify_ssl;
        // Only include secret if provided (for updates)
        if (form.proxmox_token_secret) {
          payload.proxmox_token_secret = form.proxmox_token_secret;
        }
      } else if (form.hypervisor_type === 'hyperv') {
        payload.hyperv_host = form.hyperv_host;
        payload.hyperv_port = parseInt(form.hyperv_port) || 5986;
        payload.hyperv_username = form.hyperv_username;
        payload.hyperv_use_ssl = form.hyperv_use_ssl;
        payload.hyperv_verify_ssl = form.hyperv_verify_ssl;
        // Only include password if provided
        if (form.hyperv_password) {
          payload.hyperv_password = form.hyperv_password;
        }
      }

      if (editingHypervisor) {
        await api.put(`/api/hypervisors/${editingHypervisor.id}/`, payload);
      } else {
        await api.post('/api/hypervisors/', payload);
      }

      closeModal();
      fetchData();
    } catch (err) {
      console.error('Failed to save hypervisor:', err);
      const errorData = err.response?.data;
      let errorMsg = 'Failed to save hypervisor';
      
      if (errorData) {
        // Find the first error message
        const firstError = Object.values(errorData).find(v => Array.isArray(v) && v.length > 0);
        if (firstError) {
          errorMsg = firstError[0];
        } else if (errorData.error) {
          errorMsg = errorData.error;
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        }
      }
      setFormError(errorMsg);
    } finally {
      setFormLoading(false);
    }
  };

  // Delete hypervisor
  const deleteHypervisor = async (hvId) => {
    if (!confirm('Are you sure you want to delete this hypervisor configuration?\n\nThis will also remove all associated VMs from the system.')) {
      return;
    }

    try {
      await api.delete(`/api/hypervisors/${hvId}/`);
      fetchData();
    } catch (err) {
      alert('Failed to delete hypervisor: ' + (err.response?.data?.error || err.message));
    }
  };

  // Test connection
  const testConnection = async (hvId) => {
    setTestingId(hvId);
    setTestResult(null);

    try {
      const response = await api.post(`/api/hypervisors/${hvId}/test/`);
      setTestResult({
        id: hvId,
        success: true,
        message: response.data.message || 'Connection successful',
        data: response.data.data,
      });
    } catch (err) {
      setTestResult({
        id: hvId,
        success: false,
        message: err.response?.data?.details || err.response?.data?.error || 'Connection failed',
      });
    } finally {
      setTestingId(null);
    }
  };

  // Sync VMs
  const syncVMs = async (hvId) => {
    setSyncingId(hvId);
    setSyncResult(null);

    try {
      const response = await api.post(`/api/hypervisors/${hvId}/sync/`);
      const data = response.data.data || {};
      setSyncResult({
        id: hvId,
        success: true,
        message: response.data.message || 'Sync completed',
        created: data.created || 0,
        updated: data.updated || 0,
        removed: data.removed || 0,
        total: data.total || 0,
      });
      fetchData(); // Refresh to show updated sync status
    } catch (err) {
      setSyncResult({
        id: hvId,
        success: false,
        message: err.response?.data?.details || err.response?.data?.error || 'Sync failed',
      });
    } finally {
      setSyncingId(null);
    }
  };

  // Get servers without hypervisor configured (for dropdown)
  const getAvailableServers = () => {
    const configuredServerIds = hypervisors.map(hv => hv.server?.id || hv.server);
    // When editing, include the current server
    if (editingHypervisor) {
      const currentServerId = editingHypervisor.server?.id || editingHypervisor.server;
      return servers.filter(s => !configuredServerIds.includes(s.id) || s.id === currentServerId);
    }
    return servers.filter(s => !configuredServerIds.includes(s.id));
  };

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleString();
  };

  // Get hypervisor type badge
  const getTypeBadge = (type) => {
    if (type === 'proxmox') {
      return <span className="hv-type-badge proxmox">Proxmox VE</span>;
    }
    return <span className="hv-type-badge hyperv">Hyper-V</span>;
  };

  // Get sync status badge
  const getSyncStatusBadge = (status) => {
    const statusMap = {
      'success': { className: 'success', label: 'Success' },
      'failed': { className: 'error', label: 'Failed' },
      'pending': { className: 'warning', label: 'Pending' },
      'in_progress': { className: 'info', label: 'In Progress' },
    };
    const config = statusMap[status] || { className: 'default', label: status || 'Unknown' };
    return <span className={`sync-status-badge ${config.className}`}>{config.label}</span>;
  };

  // Access denied for non-admin
  if (!isAdmin) {
    return (
      <div className="hypervisors">
        <h2>🖥️ Hypervisors</h2>
        <div className="auth-notice">
          <p>🔒 Admin access required to manage hypervisor configurations.</p>
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className="hypervisors">
        <h2>🖥️ Hypervisors</h2>
        <p>Loading hypervisor data...</p>
      </div>
    );
  }

  return (
    <div className="hypervisors">
      <div className="page-header">
        <h2>🖥️ Hypervisors</h2>
        <button className="btn-primary" onClick={openNewModal}>
          + Add Hypervisor
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      {/* Tab Navigation */}
      <div className="hv-tabs">
        <button 
          className={`tab-btn ${activeTab === 'list' ? 'active' : ''}`}
          onClick={() => setActiveTab('list')}
        >
          Configurations ({hypervisors.length})
        </button>
        <button 
          className={`tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
          onClick={() => setActiveTab('stats')}
        >
          Statistics
        </button>
      </div>

      {/* List Tab */}
      {activeTab === 'list' && (
        <div className="hv-list-section">
          {hypervisors.length === 0 ? (
            <div className="empty-state">
              <p>No hypervisors configured yet.</p>
              <p>Click "Add Hypervisor" to configure a Proxmox VE or Hyper-V server.</p>
            </div>
          ) : (
            <div className="hv-grid">
              {hypervisors.map(hv => (
                <div key={hv.id} className="hv-card">
                  <div className="hv-card-header">
                    <div className="hv-title">
                      <h3>{hv.display_name || hv.server_name || 'Unnamed'}</h3>
                      {getTypeBadge(hv.hypervisor_type)}
                    </div>
                    <div className="hv-actions">
                      <button 
                        className="btn-icon" 
                        onClick={() => openEditModal(hv)}
                        title="Edit"
                      >
                        ✏️
                      </button>
                      <button 
                        className="btn-icon danger" 
                        onClick={() => deleteHypervisor(hv.id)}
                        title="Delete"
                      >
                        🗑️
                      </button>
                    </div>
                  </div>

                  <div className="hv-card-body">
                    <div className="hv-info-row">
                      <span className="label">Host Server:</span>
                      <span className="value">{hv.server_name || 'N/A'}</span>
                    </div>
                    <div className="hv-info-row">
                      <span className="label">Connection:</span>
                      <span className="value">
                        {hv.hypervisor_type === 'proxmox' 
                          ? `${hv.proxmox_host}:${hv.proxmox_port}`
                          : `${hv.hyperv_host}:${hv.hyperv_port}`
                        }
                      </span>
                    </div>
                    <div className="hv-info-row">
                      <span className="label">Sync Status:</span>
                      {getSyncStatusBadge(hv.last_sync_status)}
                    </div>
                    <div className="hv-info-row">
                      <span className="label">Last Sync:</span>
                      <span className="value">{formatDate(hv.last_sync)}</span>
                    </div>
                    <div className="hv-info-row">
                      <span className="label">VMs:</span>
                      <span className="value vm-count">
                        <span className="running">{hv.running_vm_count || 0} running</span>
                        <span className="total">/ {hv.vm_count || 0} total</span>
                      </span>
                    </div>
                    {hv.last_sync_error && (
                      <div className="hv-error">
                        <span className="label">Error:</span>
                        <span className="value">{hv.last_sync_error}</span>
                      </div>
                    )}
                  </div>

                  <div className="hv-card-footer">
                    <button
                      className="btn-secondary"
                      onClick={() => testConnection(hv.id)}
                      disabled={testingId === hv.id}
                    >
                      {testingId === hv.id ? '⏳ Testing...' : '🔌 Test'}
                    </button>
                    <button
                      className="btn-secondary"
                      onClick={() => syncVMs(hv.id)}
                      disabled={syncingId === hv.id}
                    >
                      {syncingId === hv.id ? '⏳ Syncing...' : '🔄 Sync'}
                    </button>
                  </div>

                  {/* Test Result */}
                  {testResult && testResult.id === hv.id && (
                    <div className={`hv-result ${testResult.success ? 'success' : 'error'}`}>
                      <span className="icon">{testResult.success ? '✅' : '❌'}</span>
                      <span className="message">{testResult.message}</span>
                      {testResult.data?.version && (
                        <span className="detail">Version: {testResult.data.version}</span>
                      )}
                      <button className="dismiss" onClick={() => setTestResult(null)}>×</button>
                    </div>
                  )}

                  {/* Sync Result */}
                  {syncResult && syncResult.id === hv.id && (
                    <div className={`hv-result ${syncResult.success ? 'success' : 'error'}`}>
                      <span className="icon">{syncResult.success ? '✅' : '❌'}</span>
                      <span className="message">{syncResult.message}</span>
                      {syncResult.success && (
                        <span className="detail">
                          +{syncResult.created} created, ~{syncResult.updated} updated, -{syncResult.removed} removed
                        </span>
                      )}
                      <button className="dismiss" onClick={() => setSyncResult(null)}>×</button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Stats Tab */}
      {activeTab === 'stats' && (
        <div className="hv-stats-section">
          {stats ? (
            <div className="stats-grid">
              <div className="stat-card">
                <span className="stat-value">{stats.total_hypervisors || 0}</span>
                <span className="stat-label">Total Hypervisors</span>
              </div>
              <div className="stat-card">
                <span className="stat-value proxmox">{stats.by_type?.proxmox || 0}</span>
                <span className="stat-label">Proxmox VE</span>
              </div>
              <div className="stat-card">
                <span className="stat-value hyperv">{stats.by_type?.hyperv || 0}</span>
                <span className="stat-label">Hyper-V</span>
              </div>
              <div className="stat-card">
                <span className="stat-value">{stats.total_vms || 0}</span>
                <span className="stat-label">Total VMs</span>
              </div>
              <div className="stat-card">
                <span className="stat-value success">{stats.sync_success || 0}</span>
                <span className="stat-label">Sync Success</span>
              </div>
              <div className="stat-card">
                <span className="stat-value error">{stats.sync_failed || 0}</span>
                <span className="stat-label">Sync Failed</span>
              </div>
            </div>
          ) : (
            <p>No statistics available.</p>
          )}
        </div>
      )}

      {/* Hypervisor Config Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content hv-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{editingHypervisor ? 'Edit Hypervisor' : 'Add Hypervisor'}</h3>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form onSubmit={saveHypervisor}>
              <div className="modal-body">
                {formError && <div className="form-error">{formError}</div>}

                {/* Server Selection (only for new) */}
                {!editingHypervisor && (
                  <div className="form-group">
                    <label>Host Server *</label>
                    <select
                      value={form.server}
                      onChange={(e) => setForm({ ...form, server: e.target.value })}
                      required
                    >
                      <option value="">Select a server...</option>
                      {getAvailableServers().length === 0 ? (
                        <option value="" disabled>
                          {servers.length === 0 
                            ? "No servers found - discover servers first" 
                            : "All servers have hypervisors configured"}
                        </option>
                      ) : (
                        getAvailableServers().map(server => (
                          <option key={server.id} value={server.id}>
                            {server.server_name || server.name} ({server.ip || 'No IP'})
                          </option>
                        ))
                      )}
                    </select>
                    <small>
                      {getAvailableServers().length === 0 && servers.length === 0 
                        ? "Go to the Servers page and click 'Discover Servers' first" 
                        : "Select the physical server running the hypervisor"}
                    </small>
                  </div>
                )}

                {/* Hypervisor Type */}
                <div className="form-group">
                  <label>Hypervisor Type *</label>
                  <select
                    value={form.hypervisor_type}
                    onChange={(e) => setForm({ ...form, hypervisor_type: e.target.value })}
                    disabled={!!editingHypervisor}
                  >
                    <option value="proxmox">Proxmox VE</option>
                    <option value="hyperv">Windows Hyper-V</option>
                  </select>
                </div>

                {/* Display Name */}
                <div className="form-group">
                  <label>Display Name</label>
                  <input
                    type="text"
                    value={form.display_name}
                    onChange={(e) => setForm({ ...form, display_name: e.target.value })}
                    placeholder="Optional custom name"
                  />
                </div>

                {/* Proxmox Fields */}
                {form.hypervisor_type === 'proxmox' && (
                  <div className="form-section">
                    <h4>Proxmox Connection</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>API Host *</label>
                        <input
                          type="text"
                          value={form.proxmox_host}
                          onChange={(e) => setForm({ ...form, proxmox_host: e.target.value })}
                          placeholder="pve.example.com"
                          required
                        />
                      </div>
                      <div className="form-group small">
                        <label>Port</label>
                        <input
                          type="number"
                          value={form.proxmox_port}
                          onChange={(e) => setForm({ ...form, proxmox_port: e.target.value })}
                          min="1"
                          max="65535"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Node Name *</label>
                      <input
                        type="text"
                        value={form.proxmox_node}
                        onChange={(e) => setForm({ ...form, proxmox_node: e.target.value })}
                        placeholder="pve"
                        required
                      />
                      <small>The Proxmox node name (usually 'pve')</small>
                    </div>

                    <div className="form-group">
                      <label>API Token ID *</label>
                      <input
                        type="text"
                        value={form.proxmox_token_id}
                        onChange={(e) => setForm({ ...form, proxmox_token_id: e.target.value })}
                        placeholder="user@pam!tokenname"
                        required
                      />
                      <small>Format: user@realm!tokenname</small>
                    </div>

                    <div className="form-group">
                      <label>API Token Secret {editingHypervisor ? '(leave blank to keep current)' : '*'}</label>
                      <input
                        type="password"
                        value={form.proxmox_token_secret}
                        onChange={(e) => setForm({ ...form, proxmox_token_secret: e.target.value })}
                        placeholder="••••••••"
                        required={!editingHypervisor}
                      />
                    </div>

                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.proxmox_verify_ssl}
                          onChange={(e) => setForm({ ...form, proxmox_verify_ssl: e.target.checked })}
                        />
                        Verify SSL Certificate
                      </label>
                      <small>Disable for self-signed certificates</small>
                    </div>
                  </div>
                )}

                {/* Hyper-V Fields */}
                {form.hypervisor_type === 'hyperv' && (
                  <div className="form-section">
                    <h4>Hyper-V Connection (WinRM)</h4>
                    
                    <div className="form-row">
                      <div className="form-group">
                        <label>Host *</label>
                        <input
                          type="text"
                          value={form.hyperv_host}
                          onChange={(e) => setForm({ ...form, hyperv_host: e.target.value })}
                          placeholder="hyperv.example.com"
                          required
                        />
                      </div>
                      <div className="form-group small">
                        <label>Port</label>
                        <input
                          type="number"
                          value={form.hyperv_port}
                          onChange={(e) => setForm({ ...form, hyperv_port: e.target.value })}
                          min="1"
                          max="65535"
                        />
                      </div>
                    </div>

                    <div className="form-group">
                      <label>Username *</label>
                      <input
                        type="text"
                        value={form.hyperv_username}
                        onChange={(e) => setForm({ ...form, hyperv_username: e.target.value })}
                        placeholder="Administrator"
                        required
                      />
                      <small>Windows username with Hyper-V permissions</small>
                    </div>

                    <div className="form-group">
                      <label>Password {editingHypervisor ? '(leave blank to keep current)' : '*'}</label>
                      <input
                        type="password"
                        value={form.hyperv_password}
                        onChange={(e) => setForm({ ...form, hyperv_password: e.target.value })}
                        placeholder="••••••••"
                        required={!editingHypervisor}
                      />
                    </div>

                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.hyperv_use_ssl}
                          onChange={(e) => setForm({ ...form, hyperv_use_ssl: e.target.checked })}
                        />
                        Use SSL (HTTPS)
                      </label>
                      <small>Port 5986 for HTTPS, 5985 for HTTP</small>
                    </div>

                    <div className="form-group checkbox">
                      <label>
                        <input
                          type="checkbox"
                          checked={form.hyperv_verify_ssl}
                          onChange={(e) => setForm({ ...form, hyperv_verify_ssl: e.target.checked })}
                        />
                        Verify SSL Certificate
                      </label>
                      <small>Usually disabled for self-signed WinRM certs</small>
                    </div>
                  </div>
                )}

                {/* Sync Configuration */}
                <div className="form-section">
                  <h4>Sync Configuration</h4>

                  <div className="form-group checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={form.sync_enabled}
                        onChange={(e) => setForm({ ...form, sync_enabled: e.target.checked })}
                      />
                      Enable Automatic Sync
                    </label>
                  </div>

                  <div className="form-group">
                    <label>Sync Interval (minutes)</label>
                    <input
                      type="number"
                      value={form.sync_interval_minutes}
                      onChange={(e) => setForm({ ...form, sync_interval_minutes: e.target.value })}
                      min="5"
                      max="1440"
                    />
                    <small>How often to automatically sync VM data (5-1440 minutes)</small>
                  </div>
                </div>
              </div>

              <div className="modal-footer">
                <button type="button" className="btn-secondary" onClick={closeModal}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={formLoading}>
                  {formLoading ? 'Saving...' : (editingHypervisor ? 'Update' : 'Create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

// =============================================================================
// Virtual Machines Component
// =============================================================================

function VirtualMachines() {
  const [vms, setVms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  
  // Pagination state
  const [page, setPage] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  
  // Get URL search params for pre-filtering
  const [searchParams, setSearchParams] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return {
      host_server: params.get('host_server') || '',
    };
  });
  
  // Filter state - initialize with URL params
  const [filters, setFilters] = useState({
    state: '',
    hypervisor_type: '',
    search: '',
    sort: '-updated_at',
    host_server: searchParams.host_server,
  });
  
  // Track the server name for display when filtering by server
  const [filterServerName, setFilterServerName] = useState('');
  
  // Detail modal state
  const [selectedVm, setSelectedVm] = useState(null);
  const [vmDetail, setVmDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  
  const { isAuthenticated, isAdmin } = useAuth();
  const navigate = useNavigate();

  // Fetch server name if filtering by server
  useEffect(() => {
    if (filters.host_server) {
      api.get(`/api/servers/${filters.host_server}/`)
        .then(response => {
          setFilterServerName(response.data.server_name || response.data.name || 'Unknown Server');
        })
        .catch(() => {
          setFilterServerName('');
        });
    } else {
      setFilterServerName('');
    }
  }, [filters.host_server]);

  // Fetch VMs with filters
  useEffect(() => {
    if (isAuthenticated) {
      fetchVMs();
      fetchStats();
    } else {
      setLoading(false);
    }
  }, [isAuthenticated, page, filters]);

  const fetchVMs = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: pageSize.toString(),
        sort: filters.sort,
      });
      
      if (filters.state) params.append('state', filters.state);
      if (filters.hypervisor_type) params.append('hypervisor_type', filters.hypervisor_type);
      if (filters.search) params.append('search', filters.search);
      if (filters.host_server) params.append('host_server', filters.host_server);
      
      const response = await api.get(`/api/vms/?${params.toString()}`);
      setVms(response.data.results || []);
      setTotalCount(response.data.count || 0);
      setTotalPages(Math.ceil((response.data.count || 0) / pageSize));
    } catch (err) {
      console.error('Failed to fetch VMs:', err);
      setError('Failed to load virtual machines');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/api/vms/stats/');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch VM stats:', err);
    }
  };

  const fetchVmDetail = async (vm) => {
    setSelectedVm(vm);
    setDetailLoading(true);
    try {
      const response = await api.get(`/api/vms/${vm.id}/`);
      setVmDetail(response.data);
    } catch (err) {
      console.error('Failed to fetch VM details:', err);
      setVmDetail(null);
    } finally {
      setDetailLoading(false);
    }
  };

  const closeDetail = () => {
    setSelectedVm(null);
    setVmDetail(null);
  };

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    setPage(1); // Reset to first page on filter change
  };

  const clearFilters = () => {
    setFilters({
      state: '',
      hypervisor_type: '',
      search: '',
      sort: '-updated_at',
      host_server: '',
    });
    setPage(1);
  };

  // Format bytes to human readable
  const formatBytes = (bytes) => {
    if (!bytes || bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (!isAuthenticated) {
    return (
      <div className="vms-page">
        <h2>Virtual Machines</h2>
        <p className="auth-notice">🔒 <Link to="/login">Sign in</Link> to view virtual machines.</p>
      </div>
    );
  }

  return (
    <div className="vms-page">
      <div className="vms-header">
        <h2>Virtual Machines</h2>
        <button onClick={() => { fetchVMs(); fetchStats(); }} className="btn-primary" disabled={loading}>
          {loading ? 'Refreshing...' : '🔄 Refresh'}
        </button>
      </div>

      {/* Server Filter Banner */}
      {filters.host_server && (
        <div className="server-filter-banner">
          <span className="filter-icon">🔍</span>
          <span className="filter-text">
            Showing VMs on server: <strong>{filterServerName || 'Loading...'}</strong>
          </span>
          <button 
            className="clear-filter-btn"
            onClick={() => {
              setFilters(prev => ({ ...prev, host_server: '' }));
              // Clear URL param
              window.history.replaceState({}, '', '/vms');
            }}
          >
            × Clear filter
          </button>
        </div>
      )}

      {/* Statistics Summary */}
      {stats && (
        <div className="vms-stats-bar">
          <div className="stat-item">
            <span className="stat-value">{stats.total_vms || 0}</span>
            <span className="stat-label">Total VMs</span>
          </div>
          <div className="stat-item running">
            <span className="stat-value">{stats.running_count || 0}</span>
            <span className="stat-label">Running</span>
          </div>
          <div className="stat-item stopped">
            <span className="stat-value">{stats.stopped_count || 0}</span>
            <span className="stat-label">Stopped</span>
          </div>
          {stats.by_hypervisor && (
            <>
              <div className="stat-item proxmox">
                <span className="stat-value">{stats.by_hypervisor.proxmox || 0}</span>
                <span className="stat-label">Proxmox</span>
              </div>
              <div className="stat-item hyperv">
                <span className="stat-value">{stats.by_hypervisor.hyperv || 0}</span>
                <span className="stat-label">Hyper-V</span>
              </div>
            </>
          )}
          {stats.resource_totals && (
            <>
              <div className="stat-item">
                <span className="stat-value">{stats.resource_totals.cpu_cores || 0}</span>
                <span className="stat-label">Total vCPUs</span>
              </div>
              <div className="stat-item">
                <span className="stat-value">{stats.resource_totals.memory_gb?.toFixed(1) || 0} GB</span>
                <span className="stat-label">Total RAM</span>
              </div>
            </>
          )}
        </div>
      )}

      {/* Filter Bar */}
      <div className="vms-filter-bar">
        <div className="filter-group">
          <input
            type="text"
            placeholder="Search by name or ID..."
            value={filters.search}
            onChange={(e) => handleFilterChange('search', e.target.value)}
            className="filter-search"
          />
        </div>
        
        <div className="filter-group">
          <select
            value={filters.state}
            onChange={(e) => handleFilterChange('state', e.target.value)}
            className="filter-select"
          >
            <option value="">All States</option>
            <option value="running">Running</option>
            <option value="stopped">Stopped</option>
            <option value="paused">Paused</option>
            <option value="suspended">Suspended</option>
            <option value="unknown">Unknown</option>
          </select>
        </div>
        
        <div className="filter-group">
          <select
            value={filters.hypervisor_type}
            onChange={(e) => handleFilterChange('hypervisor_type', e.target.value)}
            className="filter-select"
          >
            <option value="">All Hypervisors</option>
            <option value="proxmox">Proxmox VE</option>
            <option value="hyperv">Hyper-V</option>
          </select>
        </div>
        
        <div className="filter-group">
          <select
            value={filters.sort}
            onChange={(e) => handleFilterChange('sort', e.target.value)}
            className="filter-select"
          >
            <option value="-updated_at">Recently Updated</option>
            <option value="updated_at">Oldest First</option>
            <option value="name">Name A-Z</option>
            <option value="-name">Name Z-A</option>
            <option value="state">State</option>
          </select>
        </div>
        
        {(filters.state || filters.hypervisor_type || filters.search) && (
          <button onClick={clearFilters} className="btn-text">
            Clear Filters
          </button>
        )}
      </div>

      {/* Results Count */}
      <div className="vms-results-info">
        {totalCount > 0 ? (
          <span>Showing {((page - 1) * pageSize) + 1}-{Math.min(page * pageSize, totalCount)} of {totalCount} virtual machines</span>
        ) : (
          <span>No virtual machines found</span>
        )}
      </div>

      {/* Error Display */}
      {error && (
        <div className="error-message">{error}</div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="vms-loading">
          <p>Loading virtual machines...</p>
        </div>
      )}

      {/* VM Grid */}
      {!loading && !error && (
        <>
          {vms.length > 0 ? (
            <div className="vms-grid">
              {vms.map(vm => (
                <VMCard key={vm.id} vm={vm} onClick={() => fetchVmDetail(vm)} />
              ))}
            </div>
          ) : (
            <div className="vms-empty">
              <p>No virtual machines found.</p>
              {(filters.state || filters.hypervisor_type || filters.search) ? (
                <p>Try adjusting your filters or <button onClick={clearFilters} className="btn-text">clear all filters</button>.</p>
              ) : isAdmin ? (
                <p>Configure a <Link to="/hypervisors">hypervisor</Link> and sync VMs to see them here.</p>
              ) : (
                <p>Contact an administrator to configure hypervisor integration.</p>
              )}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="vms-pagination">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page === 1}
                className="btn-secondary"
              >
                ← Previous
              </button>
              <span className="page-info">
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="btn-secondary"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* VM Detail Modal */}
      {selectedVm && (
        <VMDetailModal
          vm={selectedVm}
          detail={vmDetail}
          loading={detailLoading}
          onClose={closeDetail}
          formatBytes={formatBytes}
          formatTimestamp={formatTimestamp}
        />
      )}
    </div>
  );
}

// =============================================================================
// VM Card Component
// =============================================================================

function VMCard({ vm, onClick }) {
  const getStateClass = (state) => {
    switch (state) {
      case 'running': return 'state-running';
      case 'stopped': return 'state-stopped';
      case 'paused': return 'state-paused';
      case 'suspended': return 'state-suspended';
      default: return 'state-unknown';
    }
  };

  const getStateIcon = (state) => {
    switch (state) {
      case 'running': return '🟢';
      case 'stopped': return '⭕';
      case 'paused': return '⏸️';
      case 'suspended': return '💤';
      default: return '❓';
    }
  };

  const getHypervisorBadge = (type) => {
    switch (type) {
      case 'proxmox': return <span className="hv-badge hv-proxmox">Proxmox</span>;
      case 'hyperv': return <span className="hv-badge hv-hyperv">Hyper-V</span>;
      default: return null;
    }
  };

  // Get primary IP address
  const getPrimaryIp = () => {
    if (!vm.ip_addresses) return 'N/A';
    if (Array.isArray(vm.ip_addresses)) {
      return vm.ip_addresses[0] || 'N/A';
    }
    if (typeof vm.ip_addresses === 'string') {
      return vm.ip_addresses.split(',')[0] || vm.ip_addresses || 'N/A';
    }
    return 'N/A';
  };

  return (
    <div className={`vm-card ${getStateClass(vm.state)}`} onClick={onClick}>
      <div className="vm-card-header">
        <div className="vm-name-section">
          <span className="vm-state-icon">{getStateIcon(vm.state)}</span>
          <h3 className="vm-name">{vm.name}</h3>
        </div>
        {getHypervisorBadge(vm.hypervisor_type)}
      </div>

      <div className="vm-card-info">
        <div className="info-row">
          <span className="label">Host:</span>
          <span className="value">{vm.host_server_name || 'Unknown'}</span>
        </div>
        <div className="info-row">
          <span className="label">IP:</span>
          <span className="value">{getPrimaryIp()}</span>
        </div>
        <div className="info-row">
          <span className="label">State:</span>
          <span className={`value state-text ${getStateClass(vm.state)}`}>
            {vm.state_display || vm.state}
          </span>
        </div>
      </div>

      {/* Resource Configuration */}
      <div className="vm-card-resources">
        <div className="resource-item">
          <span className="resource-icon">🖥️</span>
          <span className="resource-value">{vm.cpu_cores || 0} vCPU</span>
        </div>
        <div className="resource-item">
          <span className="resource-icon">💾</span>
          <span className="resource-value">{vm.memory_gb?.toFixed(1) || (vm.memory_mb ? (vm.memory_mb / 1024).toFixed(1) : 0)} GB</span>
        </div>
      </div>

      {/* Usage Metrics (only for running VMs) */}
      {vm.state === 'running' && (
        <div className="vm-card-metrics">
          {/* CPU Usage Bar */}
          <div className="metric-bar">
            <div className="metric-bar-header">
              <span className="metric-label">CPU</span>
              <span className="metric-value">{vm.cpu_usage_percent?.toFixed(1) || 0}%</span>
            </div>
            <div className="metric-bar-track">
              <div 
                className={`metric-bar-fill ${getUsageClass(vm.cpu_usage_percent)}`}
                style={{ width: `${Math.min(vm.cpu_usage_percent || 0, 100)}%` }}
              />
            </div>
          </div>

          {/* Memory Usage Bar */}
          <div className="metric-bar">
            <div className="metric-bar-header">
              <span className="metric-label">Memory</span>
              <span className="metric-value">{vm.memory_usage_percent?.toFixed(1) || 0}%</span>
            </div>
            <div className="metric-bar-track">
              <div 
                className={`metric-bar-fill ${getUsageClass(vm.memory_usage_percent)}`}
                style={{ width: `${Math.min(vm.memory_usage_percent || 0, 100)}%` }}
              />
            </div>
          </div>
        </div>
      )}

      <div className="vm-card-footer">
        <span className="vm-updated">
          Updated: {vm.updated_at ? new Date(vm.updated_at).toLocaleString() : 'Never'}
        </span>
        <span className="click-hint">Details →</span>
      </div>
    </div>
  );
}

// Helper function for usage bar colors
function getUsageClass(percent) {
  if (!percent || percent < 60) return 'usage-normal';
  if (percent < 80) return 'usage-warning';
  return 'usage-critical';
}

// =============================================================================
// VM Detail Modal Component
// =============================================================================

function VMDetailModal({ vm, detail, loading, onClose, formatBytes, formatTimestamp, onNavigateToServer }) {
  // Use detail if loaded, otherwise use vm for basic info
  const data = detail || vm;
  const navigate = useNavigate();
  
  // Tab state
  const [activeTab, setActiveTab] = useState('info');
  
  // Metrics state
  const [metricsData, setMetricsData] = useState(null);
  const [metricsLoading, setMetricsLoading] = useState(false);
  const [metricsError, setMetricsError] = useState(null);
  const [metricsRange, setMetricsRange] = useState('24h');
  
  // Auto-refresh state for metrics
  const [autoRefreshEnabled, setAutoRefreshEnabled] = useState(false);
  const AUTO_REFRESH_INTERVAL = 30000; // 30 seconds
  
  // Time range mapping
  const rangeToHours = {
    '1h': 1,
    '6h': 6,
    '24h': 24,
    '7d': 168,
  };

  // Auto-refresh hook for metrics
  const {
    isRefreshing: isAutoRefreshing,
    lastRefresh,
    isEnabled: autoRefreshActive,
    toggle: toggleAutoRefresh,
    stop: stopAutoRefresh,
  } = useAutoRefresh(
    async () => {
      if (data?.id && activeTab === 'metrics' && data?.state === 'running') {
        const hours = rangeToHours[metricsRange] || 24;
        const limit = metricsRange === '7d' ? 500 : 200;
        const response = await api.get(`/api/vms/${data.id}/metrics/?hours=${hours}&limit=${limit}`);
        
        const metrics = (response.data.metrics || []).reverse();
        const chartData = metrics.map((m) => ({
          time: new Date(m.collected_at).getTime(),
          timeLabel: formatChartTime(m.collected_at, metricsRange),
          cpu: m.cpu_usage_percent || 0,
          memory: m.memory_usage_percent || 0,
          disk: m.disk_usage_percent || 0,
          networkIn: m.network_in_bytes || 0,
          networkOut: m.network_out_bytes || 0,
          diskRead: m.disk_read_bytes || 0,
          diskWrite: m.disk_write_bytes || 0,
        }));
        
        setMetricsData({
          chartData,
          count: response.data.count || 0,
          vmName: response.data.vm_name,
          hours: response.data.hours,
        });
      }
    },
    { interval: AUTO_REFRESH_INTERVAL, enabled: autoRefreshEnabled }
  );

  // Stop auto-refresh when tab changes or modal closes
  useEffect(() => {
    if (activeTab !== 'metrics') {
      setAutoRefreshEnabled(false);
    }
    return () => {
      setAutoRefreshEnabled(false);
    };
  }, [activeTab]);

  // Fetch metrics when tab switches or range changes
  useEffect(() => {
    if (activeTab === 'metrics' && data?.id) {
      fetchMetrics();
    }
  }, [activeTab, metricsRange, data?.id]);

  const fetchMetrics = async () => {
    if (!data?.id) return;
    
    setMetricsLoading(true);
    setMetricsError(null);
    
    try {
      const hours = rangeToHours[metricsRange] || 24;
      const limit = metricsRange === '7d' ? 500 : 200;
      const response = await api.get(`/api/vms/${data.id}/metrics/?hours=${hours}&limit=${limit}`);
      
      // Transform data for charts - reverse to chronological order
      const metrics = (response.data.metrics || []).reverse();
      
      // Process metrics for charts
      const chartData = metrics.map((m, idx) => ({
        time: new Date(m.collected_at).getTime(),
        timeLabel: formatChartTime(m.collected_at, metricsRange),
        cpu: m.cpu_usage_percent || 0,
        memory: m.memory_usage_percent || 0,
        disk: m.disk_usage_percent || 0,
        networkIn: m.network_in_bytes || 0,
        networkOut: m.network_out_bytes || 0,
        diskRead: m.disk_read_bytes || 0,
        diskWrite: m.disk_write_bytes || 0,
      }));
      
      setMetricsData({
        chartData,
        count: response.data.count || 0,
        vmName: response.data.vm_name,
        hours: response.data.hours,
      });
    } catch (err) {
      console.error('Failed to fetch VM metrics:', err);
      setMetricsError('Failed to load metrics history');
    } finally {
      setMetricsLoading(false);
    }
  };

  // Handle auto-refresh toggle
  const handleAutoRefreshToggle = () => {
    setAutoRefreshEnabled(prev => !prev);
  };

  // Format last refresh time
  const formatLastRefresh = () => {
    if (!lastRefresh) return null;
    const now = new Date();
    const diff = Math.floor((now - lastRefresh) / 1000);
    if (diff < 60) return `${diff}s ago`;
    return `${Math.floor(diff / 60)}m ago`;
  };

  // Format time for chart axis based on range
  const formatChartTime = (timestamp, range) => {
    const date = new Date(timestamp);
    if (range === '1h' || range === '6h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (range === '24h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric', hour: '2-digit' });
    }
  };

  // Format axis tick
  const formatAxisTime = (time) => {
    const date = new Date(time);
    if (metricsRange === '1h' || metricsRange === '6h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (metricsRange === '24h') {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else {
      return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
    }
  };

  // Custom tooltip for charts
  const MetricsTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;
    
    const time = new Date(label);
    return (
      <div className="vm-metrics-tooltip">
        <div className="tooltip-time">
          {time.toLocaleString()}
        </div>
        {payload.map((entry, index) => (
          <div key={index} className="tooltip-item" style={{ color: entry.color }}>
            <span className="tooltip-name">{entry.name}:</span>
            <span className="tooltip-value">
              {entry.name.includes('Network') || entry.name.includes('Disk I/O') 
                ? formatBytes(entry.value)
                : `${entry.value.toFixed(1)}%`}
            </span>
          </div>
        ))}
      </div>
    );
  };

  const handleServerClick = () => {
    if (onNavigateToServer && data.host_server) {
      onClose();
      onNavigateToServer(data.host_server);
    } else if (data.host_server) {
      onClose();
      navigate(`/servers?highlight=${data.host_server}`);
    }
  };

  // Chart colors
  const CHART_COLORS = {
    cpu: '#3b82f6',      // Blue
    memory: '#8b5cf6',   // Purple
    disk: '#f59e0b',     // Amber
    networkIn: '#10b981', // Green
    networkOut: '#ef4444', // Red
    diskRead: '#06b6d4',  // Cyan
    diskWrite: '#f97316', // Orange
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content vm-detail-modal vm-detail-modal-wide" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{data.name}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {/* Tabs */}
        <div className="modal-tabs vm-modal-tabs">
          <button 
            className={`tab-btn ${activeTab === 'info' ? 'active' : ''}`}
            onClick={() => setActiveTab('info')}
          >
            VM Info
          </button>
          <button 
            className={`tab-btn ${activeTab === 'metrics' ? 'active' : ''}`}
            onClick={() => setActiveTab('metrics')}
          >
            📈 Metrics History
          </button>
        </div>

        {loading ? (
          <div className="modal-body">
            <p>Loading VM details...</p>
          </div>
        ) : (
          <div className="modal-body">
            {/* Info Tab */}
            {activeTab === 'info' && (
              <>
                {/* Status and Basic Info */}
                <section className="detail-section">
                  <h3>Virtual Machine Information</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">State</span>
                      <span className={`status-badge vm-state-${data.state}`}>
                        {data.state_display || data.state}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">VM ID</span>
                      <span className="detail-value">{data.vm_id}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Host Server</span>
                      <span className="detail-value">
                        {data.host_server ? (
                          <button 
                            className="btn-link host-server-link" 
                            onClick={handleServerClick}
                            title="View host server"
                          >
                            {data.host_server_name || 'Unknown'} →
                          </button>
                        ) : (
                          data.host_server_name || 'Unknown'
                        )}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Hypervisor</span>
                      <span className="detail-value">{data.hypervisor_type_display || data.hypervisor_type}</span>
                    </div>
                    {data.os_type && (
                      <div className="detail-item">
                        <span className="detail-label">OS Type</span>
                        <span className="detail-value">{data.os_type}</span>
                      </div>
                    )}
                    {data.os_version && (
                      <div className="detail-item">
                        <span className="detail-label">OS Version</span>
                        <span className="detail-value">{data.os_version}</span>
                      </div>
                    )}
                  </div>
                </section>

                {/* Resource Configuration */}
                <section className="detail-section">
                  <h3>Resource Configuration</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">vCPU Cores</span>
                      <span className="detail-value">{data.cpu_cores || 0}</span>
                    </div>
                    {data.cpu_sockets && (
                      <div className="detail-item">
                        <span className="detail-label">CPU Sockets</span>
                        <span className="detail-value">{data.cpu_sockets}</span>
                      </div>
                    )}
                    <div className="detail-item">
                      <span className="detail-label">Memory</span>
                      <span className="detail-value">
                        {data.memory_gb?.toFixed(2) || (data.memory_mb ? (data.memory_mb / 1024).toFixed(2) : 0)} GB
                      </span>
                    </div>
                    {data.disk_gb && (
                      <div className="detail-item">
                        <span className="detail-label">Disk</span>
                        <span className="detail-value">{data.disk_gb.toFixed(2)} GB</span>
                      </div>
                    )}
                  </div>
                </section>

                {/* Usage Metrics (for running VMs) */}
                {data.state === 'running' && (
                  <section className="detail-section">
                    <h3>Current Usage</h3>
                    <div className="vm-usage-meters">
                      <div className="usage-meter">
                        <div className="meter-label">CPU Usage</div>
                        <div className="meter-bar-container">
                          <div 
                            className={`meter-bar ${getUsageClass(data.cpu_usage_percent)}`}
                            style={{ width: `${Math.min(data.cpu_usage_percent || 0, 100)}%` }}
                          />
                        </div>
                        <div className="meter-value">{data.cpu_usage_percent?.toFixed(1) || 0}%</div>
                      </div>
                      
                      <div className="usage-meter">
                        <div className="meter-label">Memory Usage</div>
                        <div className="meter-bar-container">
                          <div 
                            className={`meter-bar ${getUsageClass(data.memory_usage_percent)}`}
                            style={{ width: `${Math.min(data.memory_usage_percent || 0, 100)}%` }}
                          />
                        </div>
                        <div className="meter-value">
                          {data.memory_usage_percent?.toFixed(1) || 0}%
                          {data.memory_used_gb && ` (${data.memory_used_gb.toFixed(1)} GB used)`}
                        </div>
                      </div>
                      
                      {data.disk_usage_percent !== null && data.disk_usage_percent !== undefined && (
                        <div className="usage-meter">
                          <div className="meter-label">Disk Usage</div>
                          <div className="meter-bar-container">
                            <div 
                              className={`meter-bar ${getUsageClass(data.disk_usage_percent)}`}
                              style={{ width: `${Math.min(data.disk_usage_percent || 0, 100)}%` }}
                            />
                          </div>
                          <div className="meter-value">{data.disk_usage_percent?.toFixed(1) || 0}%</div>
                        </div>
                      )}
                    </div>
                    
                    {/* Link to metrics tab */}
                    <div className="view-metrics-link">
                      <button 
                        className="btn-link"
                        onClick={() => setActiveTab('metrics')}
                      >
                        📈 View Metrics History →
                      </button>
                    </div>
                  </section>
                )}

                {/* Network Information */}
                {(data.ip_addresses || data.mac_addresses) && (
                  <section className="detail-section">
                    <h3>Network</h3>
                    <div className="detail-grid">
                      {data.ip_addresses && (
                        <div className="detail-item full-width">
                          <span className="detail-label">IP Addresses</span>
                          <span className="detail-value">
                            {Array.isArray(data.ip_addresses) 
                              ? data.ip_addresses.join(', ') 
                              : data.ip_addresses}
                          </span>
                        </div>
                      )}
                      {data.mac_addresses && (
                        <div className="detail-item full-width">
                          <span className="detail-label">MAC Addresses</span>
                          <span className="detail-value">
                            {Array.isArray(data.mac_addresses) 
                              ? data.mac_addresses.join(', ') 
                              : data.mac_addresses}
                          </span>
                        </div>
                      )}
                      {data.network_in_bytes > 0 && (
                        <div className="detail-item">
                          <span className="detail-label">Network In</span>
                          <span className="detail-value">{formatBytes(data.network_in_bytes)}</span>
                        </div>
                      )}
                      {data.network_out_bytes > 0 && (
                        <div className="detail-item">
                          <span className="detail-label">Network Out</span>
                          <span className="detail-value">{formatBytes(data.network_out_bytes)}</span>
                        </div>
                      )}
                    </div>
                  </section>
                )}

                {/* Timestamps */}
                <section className="detail-section">
                  <h3>Timestamps</h3>
                  <div className="detail-grid">
                    <div className="detail-item">
                      <span className="detail-label">Last Synced</span>
                      <span className="detail-value">{formatTimestamp(data.last_synced)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Updated</span>
                      <span className="detail-value">{formatTimestamp(data.updated_at)}</span>
                    </div>
                    <div className="detail-item">
                      <span className="detail-label">Created</span>
                      <span className="detail-value">{formatTimestamp(data.created_at)}</span>
                    </div>
                  </div>
                </section>

                {/* Description & Tags */}
                {(data.description || data.tags) && (
                  <section className="detail-section">
                    <h3>Additional Info</h3>
                    {data.description && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Description</span>
                        <span className="detail-value">{data.description}</span>
                      </div>
                    )}
                    {data.tags && Object.keys(data.tags).length > 0 && (
                      <div className="detail-item full-width">
                        <span className="detail-label">Tags</span>
                        <div className="vm-tags">
                          {Object.entries(data.tags).map(([key, value]) => (
                            <span key={key} className="vm-tag">{key}: {value}</span>
                          ))}
                        </div>
                      </div>
                    )}
                  </section>
                )}
              </>
            )}

            {/* Metrics Tab */}
            {activeTab === 'metrics' && (
              <div className="vm-metrics-tab">
                {/* Time Range Selector and Auto-Refresh Controls */}
                <div className="vm-metrics-header">
                  <h3>Performance Metrics History</h3>
                  <div className="metrics-controls">
                    <div className="time-range-selector">
                      {['1h', '6h', '24h', '7d'].map(range => (
                        <button
                          key={range}
                          className={`range-btn ${metricsRange === range ? 'active' : ''}`}
                          onClick={() => setMetricsRange(range)}
                        >
                          {range}
                        </button>
                      ))}
                    </div>
                    
                    {/* Auto-refresh controls (only for running VMs) */}
                    {data.state === 'running' && (
                      <div className="auto-refresh-control">
                        <button
                          className={`auto-refresh-btn ${autoRefreshEnabled ? 'active' : ''}`}
                          onClick={handleAutoRefreshToggle}
                          title={autoRefreshEnabled ? 'Stop auto-refresh' : 'Start auto-refresh (30s)'}
                        >
                          {isAutoRefreshing ? (
                            <span className="refresh-spinner">⟳</span>
                          ) : autoRefreshEnabled ? (
                            '⏸️ Pause'
                          ) : (
                            '▶️ Auto'
                          )}
                        </button>
                        {autoRefreshEnabled && lastRefresh && (
                          <span className="last-refresh-time">
                            Updated {formatLastRefresh()}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {metricsLoading && !isAutoRefreshing ? (
                  <div className="vm-metrics-loading">
                    <p>Loading metrics history...</p>
                  </div>
                ) : metricsError ? (
                  <div className="vm-metrics-error">
                    <p>⚠️ {metricsError}</p>
                    <button className="btn-secondary" onClick={fetchMetrics}>
                      Retry
                    </button>
                  </div>
                ) : !metricsData || metricsData.chartData.length === 0 ? (
                  <div className="vm-metrics-empty">
                    <p>📊 No metrics history available for this time range.</p>
                    <p className="hint">
                      {data.state !== 'running' 
                        ? 'Metrics are only collected while the VM is running.'
                        : 'Metrics collection may take a few minutes to start after VM sync.'}
                    </p>
                  </div>
                ) : (
                  <div className="vm-metrics-charts">
                    {/* Metrics Summary */}
                    <div className="vm-metrics-summary">
                      <span className="metrics-count">
                        📈 {metricsData.count} data points over {metricsData.hours} hours
                      </span>
                    </div>

                    {/* CPU Usage Chart */}
                    <div className="vm-chart-card">
                      <h4>🖥️ CPU Usage</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={metricsData.chartData}>
                          <defs>
                            <linearGradient id="cpuGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={CHART_COLORS.cpu} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={CHART_COLORS.cpu} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                          <XAxis 
                            dataKey="time" 
                            tickFormatter={formatAxisTime}
                            stroke="#666"
                            fontSize={11}
                            tickCount={6}
                          />
                          <YAxis 
                            domain={[0, 100]} 
                            tickFormatter={(v) => `${v}%`}
                            stroke="#666"
                            fontSize={11}
                            width={45}
                          />
                          <Tooltip content={<MetricsTooltip />} />
                          <Area 
                            type="monotone" 
                            dataKey="cpu" 
                            stroke={CHART_COLORS.cpu}
                            strokeWidth={2}
                            fill="url(#cpuGradient)"
                            name="CPU Usage"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Memory Usage Chart */}
                    <div className="vm-chart-card">
                      <h4>💾 Memory Usage</h4>
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={metricsData.chartData}>
                          <defs>
                            <linearGradient id="memoryGradient" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={CHART_COLORS.memory} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={CHART_COLORS.memory} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                          <XAxis 
                            dataKey="time" 
                            tickFormatter={formatAxisTime}
                            stroke="#666"
                            fontSize={11}
                            tickCount={6}
                          />
                          <YAxis 
                            domain={[0, 100]} 
                            tickFormatter={(v) => `${v}%`}
                            stroke="#666"
                            fontSize={11}
                            width={45}
                          />
                          <Tooltip content={<MetricsTooltip />} />
                          <Area 
                            type="monotone" 
                            dataKey="memory" 
                            stroke={CHART_COLORS.memory}
                            strokeWidth={2}
                            fill="url(#memoryGradient)"
                            name="Memory Usage"
                          />
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>

                    {/* Disk Usage Chart */}
                    {metricsData.chartData.some(d => d.disk > 0) && (
                      <div className="vm-chart-card">
                        <h4>💿 Disk Usage</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <AreaChart data={metricsData.chartData}>
                            <defs>
                              <linearGradient id="diskGradient" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor={CHART_COLORS.disk} stopOpacity={0.3}/>
                                <stop offset="95%" stopColor={CHART_COLORS.disk} stopOpacity={0}/>
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                            <XAxis 
                              dataKey="time" 
                              tickFormatter={formatAxisTime}
                              stroke="#666"
                              fontSize={11}
                              tickCount={6}
                            />
                            <YAxis 
                              domain={[0, 100]} 
                              tickFormatter={(v) => `${v}%`}
                              stroke="#666"
                              fontSize={11}
                              width={45}
                            />
                            <Tooltip content={<MetricsTooltip />} />
                            <Area 
                              type="monotone" 
                              dataKey="disk" 
                              stroke={CHART_COLORS.disk}
                              strokeWidth={2}
                              fill="url(#diskGradient)"
                              name="Disk Usage"
                            />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Network I/O Chart */}
                    {metricsData.chartData.some(d => d.networkIn > 0 || d.networkOut > 0) && (
                      <div className="vm-chart-card">
                        <h4>🌐 Network I/O</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={metricsData.chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                            <XAxis 
                              dataKey="time" 
                              tickFormatter={formatAxisTime}
                              stroke="#666"
                              fontSize={11}
                              tickCount={6}
                            />
                            <YAxis 
                              tickFormatter={(v) => formatBytes(v)}
                              stroke="#666"
                              fontSize={11}
                              width={65}
                            />
                            <Tooltip content={<MetricsTooltip />} />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="networkIn" 
                              stroke={CHART_COLORS.networkIn}
                              strokeWidth={2}
                              dot={false}
                              name="Network In"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="networkOut" 
                              stroke={CHART_COLORS.networkOut}
                              strokeWidth={2}
                              dot={false}
                              name="Network Out"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}

                    {/* Disk I/O Chart */}
                    {metricsData.chartData.some(d => d.diskRead > 0 || d.diskWrite > 0) && (
                      <div className="vm-chart-card">
                        <h4>📀 Disk I/O</h4>
                        <ResponsiveContainer width="100%" height={200}>
                          <LineChart data={metricsData.chartData}>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e1e5eb" />
                            <XAxis 
                              dataKey="time" 
                              tickFormatter={formatAxisTime}
                              stroke="#666"
                              fontSize={11}
                              tickCount={6}
                            />
                            <YAxis 
                              tickFormatter={(v) => formatBytes(v)}
                              stroke="#666"
                              fontSize={11}
                              width={65}
                            />
                            <Tooltip content={<MetricsTooltip />} />
                            <Legend />
                            <Line 
                              type="monotone" 
                              dataKey="diskRead" 
                              stroke={CHART_COLORS.diskRead}
                              strokeWidth={2}
                              dot={false}
                              name="Disk Read"
                            />
                            <Line 
                              type="monotone" 
                              dataKey="diskWrite" 
                              stroke={CHART_COLORS.diskWrite}
                              strokeWidth={2}
                              dot={false}
                              name="Disk Write"
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        <div className="modal-footer">
          <button onClick={onClose} className="btn-secondary">Close</button>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// About Component
// =============================================================================

function About() {
  return (
    <div className="about">
      <h2>About</h2>
      <p>
        Infrastructure Management System is a comprehensive solution for monitoring
        and managing OVH infrastructure.
      </p>
      <h3>Features</h3>
      <ul>
        <li>Real-time server monitoring</li>
        <li>Automated server discovery via OVH API</li>
        <li>Performance metrics and anomaly detection</li>
        <li>Alert rules with Slack and Email notifications</li>
        <li>Alert history and management</li>
        <li>VM enumeration for Proxmox VE and Hyper-V hypervisors</li>
        <li>Comprehensive audit logging</li>
        <li>Role-based access control</li>
      </ul>
      <h3>Technology Stack</h3>
      <ul>
        <li>Backend: Django REST Framework</li>
        <li>Frontend: React + Vite</li>
        <li>Database: PostgreSQL</li>
        <li>Cache: Redis</li>
      </ul>
    </div>
  );
}

export default App
